import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Terminal,
  Search,
  ShieldCheck,
  Trophy,
  Coins,
  ThumbsUp,
  Tag,
  DollarSign,
  Wifi,
  Cpu,
  Lock,
  Crosshair,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Database,
  ListFilter,
  ExternalLink,
  ArrowLeft,
  PlayCircle,
  Zap,
  Bell,
  Mail,
  SearchX,
  CheckCircle2,
  ShieldAlert,
  Fingerprint,
  Globe,
  Heart,
  Activity,
  Scale,
  Info,
  Monitor,
  Layers,
  X,
  Radar,
  Volume2,
  VolumeX,
  RotateCcw,
  ChevronDown,
  Flame,
  Rocket,
} from "lucide-react";

// =============================================
// MANUAL COUPONS - TYPE 2 (VERIFIED, TEMPORARY)
// =============================================
// Shows badge next to price. Modal says "may expire soon"
// Format: { storeName, code, discountPercent }
// =============================================
const manualCouponsVerified = [
  // EXAMPLE: uncomment and edit below
  // { storeName: "Green Man Gaming", code: "SCAN4KEYS10", discountPercent: 10 },
];

// =============================================
// MANUAL COUPONS - TYPE 3 (UNVERIFIED, MODAL ONLY)
// =============================================
// NO badge. Modal says "not guaranteed"
// Format: { storeName, code }
// =============================================
const manualCouponsUnverified = [
  // EXAMPLE: uncomment and edit below
  // { storeName: "Fanatical", code: "TEST2025" },
];

// --- CACHE SYSTEM (14hr TTL) 🗃️ ---
const CACHE_TTL = 14 * 60 * 60 * 1000; // 14 hours in ms
const gameCache = new Map();

const getCachedGame = (id) => {
  const cached = gameCache.get(id);
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CACHE_TTL) {
    gameCache.delete(id);
    return null;
  }
  return cached.data;
};

const setCachedGame = (id, data) => {
  gameCache.set(id, { data, timestamp: Date.now() });
};

// --- SEARCH CACHE (also 14hr TTL)  ---
const searchCache = new Map();

const getCachedSearch = (query) => {
  const cached = searchCache.get(query.toLowerCase());
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CACHE_TTL) {
    searchCache.delete(query.toLowerCase());
    return null;
  }
  return cached.data;
};

const setCachedSearch = (query, data) => {
  searchCache.set(query.toLowerCase(), { data, timestamp: Date.now() });
};

// --- Serious Cyber-Audio Engine ---
const playSound = (type, audioCtx) => {
  if (!audioCtx || audioCtx.state === "suspended") return;
  const now = audioCtx.currentTime;
  const gain = audioCtx.createGain();
  gain.connect(audioCtx.destination);

  if (type === "typing") {
    const osc = audioCtx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(1500, now);
    osc.frequency.exponentialRampToValueAtTime(100, now + 0.02);
    gain.gain.setValueAtTime(0.03, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.02);
  } else if (type === "click") {
    const osc = audioCtx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(50, now + 0.1);
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.1);
  } else if (type === "hover") {
    const noiseBuffer = audioCtx.createBuffer(
      1,
      audioCtx.sampleRate * 0.02,
      audioCtx.sampleRate,
    );
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < output.length; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    const source = audioCtx.createBufferSource();
    source.buffer = noiseBuffer;
    const filter = audioCtx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = 8000;
    gain.gain.setValueAtTime(0.02, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);
    source.connect(filter);
    filter.connect(gain);
    source.start(now);
  } else if (type === "hover_game") {
    const osc = audioCtx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(1760, now + 0.05);
    gain.gain.setValueAtTime(0.04, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.05);
  } else if (type === "deal_found") {
    const osc = audioCtx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(110, now);
    osc.frequency.exponentialRampToValueAtTime(220, now + 0.4);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.5);
  } else if (type === "no_deal") {
    const osc = audioCtx.createOscillator();
    const osc2 = audioCtx.createOscillator();
    osc.type = "sawtooth";
    osc2.type = "sine";
    osc.frequency.setValueAtTime(60, now);
    osc2.frequency.setValueAtTime(57, now);
    gain.gain.setValueAtTime(0.1, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    osc.connect(gain);
    osc2.connect(gain);
    osc.start(now);
    osc2.start(now);
    osc.stop(now + 0.3);
    osc2.stop(now + 0.3);
  }
};

const tutorialMap = {
  default: "https://www.youtube.com/embed/yzCzH4uWizg",
};

const CyberLogo = () => (
  <div className="relative w-10 h-10 group cursor-pointer">
    <div className="absolute inset-0 bg-green-500/20 blur-lg group-hover:bg-green-500/40 transition-all duration-500" />
    <svg
      viewBox="0 0 100 100"
      className="w-full h-full relative z-10 animate-glitch-slow"
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        d="M20,30 L80,30 L80,70 L20,70 Z"
        fill="none"
        stroke="#22c55e"
        strokeWidth="4"
      />
      <path
        d="M35,45 L65,45 M35,55 L65,55"
        stroke="#22c55e"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <path d="M10,20 L30,20 M70,80 L90,80" stroke="#22c55e" strokeWidth="2" />
    </svg>
  </div>
);

export default function App() {
  const [query, setQuery] = useState("");
  const [logs, setLogs] = useState([]);
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanMessage, setScanMessage] = useState("INITIALIZING...");
  const [stores, setStores] = useState({});
  const [activeRedirect, setActiveRedirect] = useState(null);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    url: "",
    gameTitle: "",
    gameId: "",
    gameImage: "",
  });
  const [showLegal, setShowLegal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [email, setEmail] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAllOfficial, setShowAllOfficial] = useState(false);
  const [showAllKeySellers, setShowAllKeySellers] = useState(false);
  const [showAllMarketplaces, setShowAllMarketplaces] = useState(false);
  const [shareGameTitle, setShareGameTitle] = useState("");
  const [shareGameId, setShareGameId] = useState("");
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showHotDeals, setShowHotDeals] = useState(false);
  const [showSimilarGames, setShowSimilarGames] = useState(false);
  const [similarGamesGlitch, setSimilarGamesGlitch] = useState(false);
  const [scanGlitching, setScanGlitching] = useState(false);
  const [similarGames, setSimilarGames] = useState([]);
  const [gamePageKey, setGamePageKey] = useState(0);
  const [visibleSimilarCount, setVisibleSimilarCount] = useState(4);
  const [showAllSimilar, setShowAllSimilar] = useState(false);
  const [hotDealsTitleIndex, setHotDealsTitleIndex] = useState(0);
  const hotDealsTitles = [
    { text: "10/10 QUALITY ON SALE", icon1: "Trophy", icon2: "Tag" },
  ];
  const [titleGlitch, setTitleGlitch] = useState(false);

  // Hot deals data (4 games for now)
  const hotDealsList = [
    // ... your existing 4 games ...
    {
      id: 2317,
      title: "Uncharted: Legacy of Thieves Collection (2022)",
      price: 20.99,
      discount: 58,
      image:
        "https://imgcdn1.nexarda.com/uploads/handler.gz?compress=v1&width=328&height=440&fit=cover&padding=0&bg=&url=https%3A%2F%2Fimgcdn1.nexarda.com%2Fuploads%2F-%2F2022%2F1644521432-3f9672ca682dfd76ad0296b2407e55dad9865a7b2051744bb57cb5000a9550e5.jpg&default=nexarda.game.cover",
    },
    {
      id: 2895,
      title: "Like a Dragon Gaiden: The Man Who Erased His Name (2023)",
      price: 13.74,
      discount: 73,
      image:
        "https://imgcdn1.nexarda.com/uploads/handler.gz?compress=v1&width=328&height=440&fit=cover&padding=0&bg=&url=https%3A%2F%2Fimgcdn1.nexarda.com%2Fuploads%2F-%2F2025%2F1764621291-4cea67855e38a5c0597b24a9d3b40eeb937b95dd5074c4e43d67126056ca809d.png&default=nexarda.game.cover",
    },
    {
      id: 82,
      title: "Sniper Elite 4 (2017)",
      price: 4.79,
      discount: 92,
      image:
        "https://imgcdn1.nexarda.com/uploads/handler.gz?compress=v1&width=328&height=440&fit=cover&padding=0&bg=&url=https%3A%2F%2Fimgcdn1.nexarda.com%2Fuploads%2F-%2F2021%2F1639232053-9831ffa5593a7be9d42533e2baa9aaaecca186156477d80d638e70d5266c2de4.png&default=nexarda.game.cover",
    },
    {
      id: 2501,
      title: "Resident Evil 4 (2023)",
      price: 13.39,
      discount: 67,
      image:
        "https://imgcdn1.nexarda.com/uploads/handler.gz?compress=v1&width=328&height=440&fit=cover&padding=0&bg=&url=https%3A%2F%2Fimgcdn1.nexarda.com%2Fuploads%2F-%2F2022%2F1668117828-83a5c716cb9a3749399e8200168e7135e9e8adb047e0b48c82d14bf87230e774.png&default=nexarda.game.cover",
    },
  ];

  const [pendingRedirectDeal, setPendingRedirectDeal] = useState(null);

  const [scanError, setScanError] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastSelected, setLastSelected] = useState(null);
  const [announcement, setAnnouncement] = useState({
    text: "A dedicated page showing this week’s & month’s best game deals is in development",
    type: "announcement",
  });
  const [audioStarted, setAudioStarted] = useState(false);

  const scrollRef = useRef(null);
  const audioCtxRef = useRef(null);
  const ambientGainRef = useRef(null);
  const mainContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const shareExecutedRef = useRef(false);
  const initialLogExecutedRef = useRef(false);
  const betaCarouselIntervalRef = useRef(null);
  const marqueeAnimationRef = useRef(null);
  const youJustSavedRef = useRef(null);
  const badgeContainerRef = useRef(null);
  const similarGamesTimeoutRef = useRef(null);
  const scanCanvasRef = useRef(null);
  const scanAnimationRef = useRef(null);

  const addLog = (text, type = "info") =>
    setLogs((p) => [...p, { text, type, id: Date.now() + Math.random() }]);
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const resetApp = () => {
    setQuery("");
    setResults(null);
    setSuggestions(null);
    setLogs([]);
    setScanProgress(0);
    setScanError(false);
    setShowSimilarGames(false);
    setShowHotDeals(false);

    // Clear similar games timeout
    if (similarGamesTimeoutRef.current) {
      clearTimeout(similarGamesTimeoutRef.current);
    }

    sfx("click");
    addLog("SYSTEM REBOOT INITIATED...", "process");
    // Reset URL
    window.history.pushState({}, "", "/");
    shareExecutedRef.current = false;

    // Replay the hot deals transition after reset (with random delay 3.4s - 3.7s)
    const randomDelay = 3400 + Math.random() * 300;
    setTimeout(() => {
      const aside = document.querySelector("aside");
      if (aside) {
        aside.classList.add("animate-heavy-glitch");
        sfx("click");
        setTimeout(() => sfx("hover"), 200);
        setTimeout(() => sfx("typing"), 400);
        setTimeout(() => sfx("click"), 600);
      }

      setTimeout(() => {
        if (aside) {
          aside.classList.remove("animate-heavy-glitch");
        }
        setShowHotDeals(true);
        sfx("deal_found");
      }, 800);
    }, randomDelay);
  };

  // --- Matrix Rain Effect ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const columns = Math.floor(width / 20);
    const drops = Array(columns)
      .fill(0)
      .map(() => Math.random() * -100);

    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, width, height);

    const draw = () => {
      ctx.fillStyle = "rgba(0, 0, 0, 0.08)";
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#4ade80";
      ctx.font = "15px monospace";
      for (let i = 0; i < drops.length; i++) {
        const text = String.fromCharCode(0x30a0 + Math.random() * 96);
        if (drops[i] > 0) ctx.fillText(text, i * 20, drops[i] * 20);
        if (drops[i] * 20 > height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
    };
    const interval = setInterval(draw, 33);
    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // --- Background Drone ---
  useEffect(() => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioCtxRef.current = new AudioContext();
    const ctx = audioCtxRef.current;
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    const gain = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.value = 40;
    osc2.type = "sawtooth";
    osc2.frequency.value = 40.5;
    lfo.type = "sine";
    lfo.frequency.value = 0.1;
    lfoGain.gain.value = 10;
    lfo.connect(lfoGain);
    lfoGain.connect(osc1.frequency);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 60;
    filter.Q.value = 10;
    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    ambientGainRef.current = gain;
    gain.gain.value = 0;
    osc1.start();
    osc2.start();
    lfo.start();
    const startAudio = () => {
      if (ctx.state === "suspended") ctx.resume();
      setAudioStarted(true);
      if (soundEnabled) gain.gain.setTargetAtTime(0.04, ctx.currentTime, 3);
      window.removeEventListener("click", startAudio);
    };
    window.addEventListener("click", startAudio);
    return () => {
      osc1.stop();
      osc2.stop();
      lfo.stop();
      window.removeEventListener("click", startAudio);
    };
  }, []);

  useEffect(() => {
    if (ambientGainRef.current && audioStarted) {
      ambientGainRef.current.gain.setTargetAtTime(
        soundEnabled ? 0.04 : 0,
        audioCtxRef.current.currentTime,
        0.5,
      );
    }
  }, [soundEnabled, audioStarted]);

  const sfx = (type) => {
    if (soundEnabled) playSound(type, audioCtxRef.current);
  };

  // Helper: get manual coupon for a store (returns { type, code, discountPercent } or null)
  const getManualCoupon = (storeName) => {
    // Type 2 - verified
    const verified = manualCouponsVerified.find(
      (c) => c.storeName === storeName,
    );
    if (verified)
      return {
        type: 2,
        code: verified.code,
        discountPercent: verified.discountPercent,
      };

    // Type 3 - unverified
    const unverified = manualCouponsUnverified.find(
      (c) => c.storeName === storeName,
    );
    if (unverified)
      return { type: 3, code: unverified.code, discountPercent: null };

    return null;
  };

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await fetch("https://www.nexarda.com/api/v3/retailers");
        const data = await res.json();
        const storeMap = {};
        if (data?.results?.length > 0) {
          data.results.forEach((s) => {
            storeMap[s.id] = { name: s.name };
          });
          setStores(storeMap);
          addLog("NETWORK CONNECTION ESTABLISHED.", "success");
          addLog("WARNING: UNSTABLE BUILD DETECTED.", "error");
          addLog("BETA PHASE: SOME FEATURES MAY OFFLINE.", "process");
        }
      } catch (err) {
        addLog("STORE DATA UNAVAILABLE.", "error");
      }
    };
    fetchStores();
  }, []);
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await fetch("https://www.nexarda.com/api/v3/retailers");
        const data = await res.json();
        const storeMap = {};
        if (data?.results?.length > 0) {
          data.results.forEach((s) => {
            storeMap[s.id] = { name: s.name };
          });
          setStores(storeMap);
          addLog("NETWORK CONNECTION ESTABLISHED.", "success");
          addLog("WARNING: UNSTABLE BUILD DETECTED.", "error");
          addLog("BETA PHASE: SOME FEATURES MAY OFFLINE.", "process");
        }
      } catch (err) {
        addLog("STORE DATA UNAVAILABLE.", "error");
      }
    };
    fetchStores();
  }, []);

  // --- Add initial log on app load (prevents double log in StrictMode) ---
  useEffect(() => {
    if (!initialLogExecutedRef.current) {
      initialLogExecutedRef.current = true;
      addLog("SYSTEM INITIALIZED. READY TO SCAN.", "success");
      addLog("IMPORTANT NOTICE: WE'RE STILL IN BETA", "process");

      // 🎯 Campaign ref detection
      const refParam = new URLSearchParams(window.location.search).get("ref");
      const refPath = window.location.pathname.match(/^\/ref\/(.+)$/i)?.[1];
      const refValue = refParam || refPath;
      if (refValue) {
        fetch("https://scan4keys-backend.onrender.com/log", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "ref", ref: refValue }),
        }).catch(() => {});
        window.history.replaceState({}, "", "/");
      }

      // Heavy Glitch animation at 3.3s - ONLY if on homepage
      const randomDelay = 3400 + Math.random() * 300;
      setTimeout(() => {
        // Check if we're on homepage (no game id, no search query)
        const isHomepage =
          window.location.pathname === "/" &&
          !window.location.search.includes("id=") &&
          !window.location.search.includes("q=");

        if (isHomepage) {
          const aside = document.querySelector("aside");
          if (aside && !showHotDeals && !results) {
            aside.classList.add("animate-heavy-glitch");
            sfx("click");
            setTimeout(() => sfx("hover"), 200);
            setTimeout(() => sfx("typing"), 400);
            setTimeout(() => sfx("click"), 600);
          }

          setTimeout(() => {
            if (aside && !showHotDeals && !results) {
              aside.classList.remove("animate-heavy-glitch");
            }
            if (!showHotDeals && !results) {
              setShowHotDeals(true);
            }
            if (!showHotDeals && !results) {
              sfx("deal_found");
            }
          }, 800);
        }
      }, randomDelay);
    }
  }, []);

  // --- Read URL Parameters on Load ---
  useEffect(() => {
    // Prevent double execution in StrictMode
    if (shareExecutedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const gameId = params.get("id");
    const gameTitle = params.get("title");
    const searchQuery = params.get("q");

    // Case 1: Game ID and Title present (share link)
    if (gameId && gameTitle) {
      shareExecutedRef.current = true;
      const decodedTitle = decodeURIComponent(gameTitle);
      // Remove year like (2011) from search query for better API matching
      const cleanTitle = decodedTitle.replace(/\s*\(\d{4}\)\s*/g, "").trim();
      setQuery(cleanTitle);

      const fetchGameImage = async () => {
        try {
          const res = await fetch(
            `https://scan4keys-backend.onrender.com/search?q=${encodeURIComponent(cleanTitle)}`,
            {
              headers: {
                "x-client-key": "MySuperSecretKey2025",
              },
            },
          );
          const data = await res.json();

          const game = data?.results?.items?.find(
            (item) => String(item.game_info.id) === String(gameId),
          );

          const imageUrl =
            game?.image ||
            "https://placehold.co/200x300/0a0f0a/22c55e?text=No+Image";

          selectGame(gameId, decodedTitle, imageUrl);
        } catch (err) {
          console.error("❌ Error:", err);
          selectGame(
            gameId,
            decodedTitle,
            "https://placehold.co/200x300/0a0f0a/22c55e?text=No+Image",
          );
        }
      };

      fetchGameImage();
    }
    // Case 2: Only search query present
    else if (searchQuery) {
      shareExecutedRef.current = true;
      const decodedQuery = decodeURIComponent(searchQuery);
      setQuery(decodedQuery);

      // Trigger search after a short delay
      setTimeout(() => {
        const fakeEvent = { preventDefault: () => {} };
        handleSearch(fakeEvent);
      }, 100);
    }
  }, []);

  // --- Handle Browser Back/Forward Buttons ---
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const gameId = params.get("id");
      const gameTitle = params.get("title");
      const searchQuery = params.get("q");

      // Clear any existing results and suggestions
      setResults(null);
      setSuggestions(null);
      setLogs([]);

      // Reset share execution flag to allow new URL processing
      shareExecutedRef.current = false;

      if (gameId && gameTitle) {
        // Load game from share link
        const decodedTitle = decodeURIComponent(gameTitle);
        const cleanTitle = decodedTitle.replace(/\s*\(\d{4}\)\s*/g, "").trim();
        setQuery(cleanTitle);

        const fetchGameImage = async () => {
          try {
            const res = await fetch(
              `https://scan4keys-backend.onrender.com/search?q=${encodeURIComponent(cleanTitle)}`,
              {
                headers: {
                  "x-client-key": "MySuperSecretKey2025",
                },
              },
            );
            const data = await res.json();
            const game = data?.results?.items?.find(
              (item) => String(item.game_info.id) === String(gameId),
            );
            const imageUrl =
              game?.image ||
              "https://placehold.co/200x300/0a0f0a/22c55e?text=No+Image";
            selectGame(gameId, decodedTitle, imageUrl);
          } catch (err) {
            selectGame(
              gameId,
              decodedTitle,
              "https://placehold.co/200x300/0a0f0a/22c55e?text=No+Image",
            );
          }
        };
        fetchGameImage();
      } else if (searchQuery) {
        // Load search results
        const decodedQuery = decodeURIComponent(searchQuery);
        setQuery(decodedQuery);
        setTimeout(() => {
          const fakeEvent = { preventDefault: () => {} };
          handleSearch(fakeEvent);
        }, 100);
      } else {
        // Reset to homepage
        setQuery("");
        setResults(null);
        setSuggestions(null);
        setLoading(false);
        setSearchLoading(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Update browser tab title based on state
  useEffect(() => {
    if (results?.title) {
      document.title = `${results.title} - Scan4Keys`;
    } else if (query) {
      document.title = `Search: ${query} - Scan4Keys`;
    } else {
      document.title = `Scan4Keys - Game Deal Scanner`;
    }
  }, [results, query]);
  // Auto-scroll beta notice carousel on mobile
  useEffect(() => {
    if (!activeRedirect) return;

    const timer = setTimeout(() => {
      const carousel = document.getElementById("beta-carousel");
      if (!carousel) return;

      const items = carousel.querySelectorAll(".snap-center");
      if (items.length === 0) return;

      let currentIndex = 0;
      const totalItems = items.length;
      const dots = document.querySelectorAll("#beta-dots div");

      function updateDots() {
        dots.forEach((dot, i) => {
          if (i === currentIndex) {
            dot.classList.add("bg-yellow-400");
            dot.classList.remove("bg-yellow-500/50");
          } else {
            dot.classList.remove("bg-yellow-400");
            dot.classList.add("bg-yellow-500/50");
          }
        });
      }

      function scrollToNext() {
        currentIndex = (currentIndex + 1) % totalItems;
        const item = items[currentIndex];
        if (item) {
          item.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
        updateDots();
      }

      if (betaCarouselIntervalRef.current) {
        clearInterval(betaCarouselIntervalRef.current);
      }
      betaCarouselIntervalRef.current = setInterval(scrollToNext, 3000);

      const handleTouchStart = () => {
        if (betaCarouselIntervalRef.current) {
          clearInterval(betaCarouselIntervalRef.current);
          betaCarouselIntervalRef.current = null;
        }
      };

      const handleTouchEnd = () => {
        if (!betaCarouselIntervalRef.current) {
          betaCarouselIntervalRef.current = setInterval(scrollToNext, 3000);
        }
      };

      carousel.addEventListener("touchstart", handleTouchStart);
      carousel.addEventListener("touchend", handleTouchEnd);

      const cleanup = () => {
        carousel.removeEventListener("touchstart", handleTouchStart);
        carousel.removeEventListener("touchend", handleTouchEnd);
        if (betaCarouselIntervalRef.current) {
          clearInterval(betaCarouselIntervalRef.current);
          betaCarouselIntervalRef.current = null;
        }
      };

      window.cleanupBetaCarousel = cleanup;
    }, 100);

    return () => {
      clearTimeout(timer);
      if (window.cleanupBetaCarousel) {
        window.cleanupBetaCarousel();
        delete window.cleanupBetaCarousel;
      }
    };
  }, [activeRedirect]);

  // JavaScript infinite marquee for purple announcement (small gap between cycles)
  useEffect(() => {
    const container = document.getElementById("marquee-container");
    if (!container) return;

    // Clone content multiple times for seamless loop
    const originalContent = container.innerHTML;
    container.innerHTML = originalContent + originalContent + originalContent;

    let position = 0;
    const speed = 1.5; // pixels per frame (lower = slower)

    function animate() {
      position -= speed;
      container.style.transform = `translateX(${position}px)`;

      // Reset when half of the content has scrolled (creates small gap)
      const containerWidth = container.scrollWidth / 3;
      if (Math.abs(position) >= containerWidth + 1) {
        position = 0;
        container.style.transform = `translateX(0px)`;
      }

      marqueeAnimationRef.current = requestAnimationFrame(animate);
    }

    marqueeAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (marqueeAnimationRef.current) {
        cancelAnimationFrame(marqueeAnimationRef.current);
        marqueeAnimationRef.current = null;
      }
    };
  }, []);

  // JavaScript infinite marquee for purple announcement (small gap between cycles)
  useEffect(() => {
    const container = document.getElementById("marquee-container");
    if (!container) return;

    // Clone content multiple times for seamless loop
    const originalContent = container.innerHTML;
    container.innerHTML = originalContent + originalContent + originalContent;

    let position = 0;
    const speed = 1.5; // pixels per frame (lower = slower)

    function animate() {
      position -= speed;
      container.style.transform = `translateX(${position}px)`;

      // Reset when half of the content has scrolled (creates small gap)
      const containerWidth = container.scrollWidth / 3;
      if (Math.abs(position) >= containerWidth + 1) {
        position = 0;
        container.style.transform = `translateX(0px)`;
      }

      marqueeAnimationRef.current = requestAnimationFrame(animate);
    }

    marqueeAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (marqueeAnimationRef.current) {
        cancelAnimationFrame(marqueeAnimationRef.current);
        marqueeAnimationRef.current = null;
      }
    };
  }, []);

  // Update visible count when showAllSimilar toggles
  useEffect(() => {
    if (showAllSimilar) {
      setVisibleSimilarCount(similarGames.length);
    } else {
      setVisibleSimilarCount(4);
    }
  }, [showAllSimilar, similarGames.length]);

  // Fetch similar games when results are loaded, then show after 4 seconds with glitch
  useEffect(() => {
    const fetchSimilarGames = async () => {
      if (results?.gameId) {
        try {
          const res = await fetch(
            `https://scan4keys-backend.onrender.com/similar-games?gameId=${results.gameId}`,
            { headers: { "x-client-key": "MySuperSecretKey2025" } },
          );
          const data = await res.json();
          const similarItems = data?.results?.items?.slice(0, 4) || [];
          setSimilarGames(similarItems);

          // Clear any existing timeout
          if (similarGamesTimeoutRef.current) {
            clearTimeout(similarGamesTimeoutRef.current);
          }

          // Wait 4 seconds, then glitch and show similar games
          const gameIdAtTimeout = results.gameId;
          similarGamesTimeoutRef.current = setTimeout(() => {
            // Check if we're still on the same game page and not reset
            if (results?.gameId === gameIdAtTimeout && showHotDeals === false) {
              const aside = document.querySelector("aside");
              if (aside) {
                aside.classList.add("animate-heavy-glitch");
                sfx("click");
                setTimeout(() => sfx("hover"), 200);
                setTimeout(() => sfx("typing"), 400);
                setTimeout(() => sfx("click"), 600);
              }

              setTimeout(() => {
                if (
                  aside &&
                  results?.gameId === gameIdAtTimeout &&
                  showHotDeals === false
                ) {
                  aside.classList.remove("animate-heavy-glitch");
                }
                if (
                  showHotDeals === false &&
                  results?.gameId === gameIdAtTimeout
                ) {
                  setShowSimilarGames(true);
                  sfx("deal_found");
                }
              }, 800);
            }
          }, 4000);
        } catch (err) {
          console.error("Failed to fetch similar games:", err);
          setSimilarGames([]);
        }
      }
    };
    fetchSimilarGames();

    // Cleanup timeout when results change
    return () => {
      if (similarGamesTimeoutRef.current) {
        clearTimeout(similarGamesTimeoutRef.current);
      }
    };
  }, [results?.gameId]);
  // No rotation - single title only

  // Position the badge to align with YOU JUST SAVED
  useEffect(() => {
    if (!results || !youJustSavedRef.current || !badgeContainerRef.current)
      return;

    const updateBadgePosition = () => {
      const youJustSavedRect = youJustSavedRef.current.getBoundingClientRect();
      const badgeContainerRect =
        badgeContainerRef.current.getBoundingClientRect();
      const badge = document.getElementById("dynamic-badge");
      if (!badge) return;

      // Calculate how far YOU JUST SAVED is from the bottom of left column
      // Then set badge's bottom position accordingly
      const distanceFromBottom =
        badgeContainerRect.bottom - youJustSavedRect.bottom;

      // Set badge's bottom position (positive = move up, negative = move down)
      badge.style.bottom = `${distanceFromBottom}px`;
    };

    updateBadgePosition();

    // Update on window resize
    window.addEventListener("resize", updateBadgePosition);

    return () => {
      window.removeEventListener("resize", updateBadgePosition);
    };
  }, [results]);

  // FINAL: Web Crawler + Matrix Rain + Fat Scanline + Frame Glitch + Scan Sound
  useEffect(() => {
    if (!loading) return;

    const canvas = scanCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    // Add strong frame glitch class to main container
    const mainContainer = document.querySelector("main");
    if (mainContainer) {
      mainContainer.classList.add("animate-slow-frame-glitch");
    }

    // Matrix rain columns (OPTIMIZED - pre-generated chars)
    const matrixColumns = Math.floor(width / 20);
    const matrixDrops = [];
    const matrixChars =
      "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789";

    for (let i = 0; i < matrixColumns; i++) {
      const chars = [];
      for (let j = 0; j < 6; j++) {
        chars.push(matrixChars[Math.floor(Math.random() * matrixChars.length)]);
      }
      matrixDrops.push({
        y: Math.random() * height,
        speed: 2 + Math.random() * 6,
        chars: chars,
      });
    }

    // Fat scanline position
    let scanlineY = 0;
    let scanlineDirection = 1;
    let lastScanSound = 0;

    // Store list
    const stores = [
      { name: "STEAM", x: 0.2, y: 0.3 },
      { name: "EPIC", x: 0.8, y: 0.25 },
      { name: "GREEN_MAN", x: 0.5, y: 0.15 },
      { name: "FANATICAL", x: 0.75, y: 0.6 },
      { name: "HUMBLE", x: 0.3, y: 0.7 },
      { name: "UBISOFT", x: 0.85, y: 0.8 },
      { name: "GOG", x: 0.1, y: 0.5 },
      { name: "MICROSOFT", x: 0.6, y: 0.85 },
      { name: "BATTLE.NET", x: 0.4, y: 0.4 },
      { name: "INDIE_GALA", x: 0.9, y: 0.45 },
      { name: "GAMERSGATE", x: 0.15, y: 0.2 },
      { name: "YUPLAY", x: 0.7, y: 0.9 },
      { name: "INSTANT_GAMING", x: 0.55, y: 0.55 },
      { name: "LOADED", x: 0.25, y: 0.45 },
      { name: "GAMEBILLET", x: 0.45, y: 0.75 },
    ];

    // Web nodes
    const nodes = [];
    const numNodes = 25;
    for (let i = 0; i < numNodes; i++) {
      nodes.push({
        x: Math.random(),
        y: Math.random(),
        active: false,
        pulse: 0,
        size: 1.5 + Math.random() * 3,
      });
    }

    // Electronic pulses (reduced)
    const pulses = [];
    for (let i = 0; i < 4; i++) {
      pulses.push({
        fromNode: Math.floor(Math.random() * numNodes),
        toNode: Math.floor(Math.random() * numNodes),
        progress: Math.random(),
        speed: 0.008 + Math.random() * 0.015,
      });
    }

    let activePing = null;
    let pingTimer = 0;
    let storeIndex = 0;
    let currentStore = stores[storeIndex];
    let frame = 0;

    // Continuous scan sound (looping)
    let scanSoundInterval = null;
    if (soundEnabled && audioCtxRef.current) {
      const playScanSound = () => {
        if (!loading || !soundEnabled) return;
        const ctx = audioCtxRef.current;
        if (ctx.state === "suspended") return;
        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(180, now);
        osc.frequency.exponentialRampToValueAtTime(80, now + 0.3);
        gain.gain.setValueAtTime(0.08, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
        osc.connect(gain);
        osc.start(now);
        osc.stop(now + 0.3);
      };
      scanSoundInterval = setInterval(() => {
        if (loading && soundEnabled) playScanSound();
      }, 800);
    }

    let resizeTimeout;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        width = canvas.width = canvas.offsetWidth;
        height = canvas.height = canvas.offsetHeight;
      }, 100);
    });
    resizeObserver.observe(canvas.parentElement || canvas);

    const animate = () => {
      if (!loading) {
        if (scanAnimationRef.current)
          cancelAnimationFrame(scanAnimationRef.current);
        if (scanSoundInterval) clearInterval(scanSoundInterval);
        if (mainContainer)
          mainContainer.classList.remove("animate-slow-frame-glitch");
        return;
      }

      ctx.clearRect(0, 0, width, height);

      // Subtle green tint
      ctx.fillStyle = "rgba(34, 197, 94, 0.02)";
      ctx.fillRect(0, 0, width, height);

      // MATRIX RAIN (OPTIMIZED - pre-generated chars, no per-frame random)
      ctx.font = "11px monospace";
      for (let i = 0; i < matrixColumns; i += 3) {
        const x = i * 20;
        const drop = matrixDrops[i];
        if (!drop) continue;

        // Draw 4 characters per column
        for (let j = 0; j < 4; j++) {
          const y = drop.y - j * 20;
          if (y > 0 && y < height && y < height + 100) {
            const brightness = j === 0 ? 0.5 : j === 1 ? 0.3 : 0.12;
            ctx.fillStyle = `rgba(34, 197, 94, ${brightness})`;
            ctx.fillText(drop.chars[j] || "0", x, y);
          }
        }

        drop.y += drop.speed;

        if (drop.y > height + 100) {
          drop.y = -50;
          for (let j = 0; j < 4; j++) {
            drop.chars[j] =
              matrixChars[Math.floor(Math.random() * matrixChars.length)];
          }
        }
      }

      // FAT SCANLINE (moving up and down)
      // Random scanline speed between 8 and 15 (fast to very fast)
      const scanlineSpeed = 7 + Math.random() * 3;
      scanlineY += scanlineDirection * scanlineSpeed;
      if (scanlineY > height) {
        scanlineY = 0;
        scanlineDirection = 1;
      }
      if (scanlineY < 0) {
        scanlineY = height;
        scanlineDirection = -1;
      }

      ctx.fillStyle = "rgba(34, 197, 94, 0.2)";
      ctx.fillRect(0, scanlineY - 10, width, 20);
      ctx.fillStyle = "rgba(34, 197, 94, 0.5)";
      ctx.fillRect(0, scanlineY - 2, width, 4);
      ctx.fillStyle = "#22c55e";
      ctx.fillRect(0, scanlineY, width, 1.5);

      // Scan sound on center pass
      const centerThreshold = height / 2;
      if (
        Math.abs(scanlineY - centerThreshold) < 8 &&
        Date.now() - lastScanSound > 350
      ) {
        lastScanSound = Date.now();
        if (soundEnabled) sfx("typing");
      }

      // CIRCUIT GRID (very subtle)
      ctx.strokeStyle = "#22c55e08";
      ctx.lineWidth = 0.5;
      for (let i = 0; i < width; i += 50) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(width, i);
        ctx.stroke();
      }

      // WEB NODES (smaller, less distracting)
      nodes.forEach((node) => {
        const x = node.x * width;
        const y = node.y * height;
        if (Math.random() < 0.002 && !node.active) {
          node.active = true;
          node.pulse = 1;
        }
        if (node.active) {
          node.pulse -= 0.02;
          if (node.pulse <= 0) {
            node.active = false;
            node.pulse = 0;
          }
        }
        if (node.active) {
          ctx.beginPath();
          ctx.arc(x, y, node.size * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(34, 197, 94, ${node.pulse * 0.2})`;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(x, y, node.size, 0, Math.PI * 2);
        ctx.fillStyle = node.active ? "#22c55e80" : "#22c55e20";
        ctx.fill();
      });

      // ELECTRONIC PULSES (fewer)
      pulses.forEach((pulse) => {
        const fromNode = nodes[pulse.fromNode];
        const toNode = nodes[pulse.toNode];
        if (!fromNode || !toNode) return;
        const fromX = fromNode.x * width;
        const fromY = fromNode.y * height;
        const toX = toNode.x * width;
        const toY = toNode.y * height;
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.strokeStyle = "#22c55e10";
        ctx.stroke();
        const progress = pulse.progress;
        const currentX = fromX + (toX - fromX) * progress;
        const currentY = fromY + (toY - fromY) * progress;
        ctx.beginPath();
        ctx.arc(currentX, currentY, 2, 0, Math.PI * 2);
        ctx.fillStyle = "#22c55e60";
        ctx.fill();
        pulse.progress += pulse.speed;
        if (pulse.progress >= 1) {
          pulse.progress = 0;
          pulse.fromNode = Math.floor(Math.random() * numNodes);
          pulse.toNode = Math.floor(Math.random() * numNodes);
        }
      });

      // STORE PINGS
      pingTimer++;
      if (pingTimer > 20) {
        pingTimer = 0;
        storeIndex = (storeIndex + 1) % stores.length;
        currentStore = stores[storeIndex];
        activePing = {
          x: currentStore.x * width,
          y: currentStore.y * height,
          radius: 5,
          intensity: 1,
        };
      }

      if (activePing) {
        activePing.radius += 8;
        activePing.intensity -= 0.03;
        for (let i = 0; i < 2; i++) {
          const offsetRadius = activePing.radius - i * 12;
          if (offsetRadius > 0) {
            ctx.beginPath();
            ctx.arc(activePing.x, activePing.y, offsetRadius, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(34, 197, 94, ${activePing.intensity * (1 - i * 0.4)})`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        }
        ctx.font = "bold 9px monospace";
        ctx.fillStyle = `rgba(34, 197, 94, ${activePing.intensity})`;
        ctx.fillText(
          `▶ ${currentStore.name}`,
          activePing.x + 12,
          activePing.y - 8,
        );
        if (activePing.intensity <= 0) activePing = null;
      }

      frame++;
      scanAnimationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (scanAnimationRef.current)
        cancelAnimationFrame(scanAnimationRef.current);
      if (scanSoundInterval) clearInterval(scanSoundInterval);
      if (mainContainer)
        mainContainer.classList.remove("animate-slow-frame-glitch");
      resizeObserver.disconnect();
    };
  }, [loading, soundEnabled]);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!query) return;
    sfx("click");
    setSearchLoading(true);
    setResults(null);
    setSuggestions(null);
    setLogs([]);
    setScanError(false);
    setShowSimilarGames(false);
    setShowHotDeals(false);
    setSimilarGames([]);
    setSimilarGamesGlitch(false);

    // 🔄 Check search cache first
    const cachedSearch = getCachedSearch(query);
    if (cachedSearch) {
      addLog(
        `SEARCH CACHE HIT: "${query}" (results from ${new Date(cachedSearch.timestamp).toLocaleTimeString()})`,
        "success",
      );
      if (!cachedSearch.items?.length) {
        setResults({ error: "not_found", title: query });
        sfx("no_deal");
        addLog("GAME NOT FOUND IN DATABASE.", "error");
      } else {
        setSuggestions(cachedSearch.items);
        sfx("deal_found");
        addLog(
          `CACHED: ${cachedSearch.items.length} MATCHES FOUND.`,
          "success",
        );

        // Update URL without reload
        const newUrl = `/?q=${encodeURIComponent(query)}`;
        window.history.pushState({}, "", newUrl);
      }
      setSearchLoading(false);
      return;
    }

    addLog(`SEARCH: "${query}" (fresh query)`, "process");
    try {
      const res = await fetch(
        `https://scan4keys-backend.onrender.com/search?q=${encodeURIComponent(query)}`,
        {
          headers: {
            "x-client-key": "MySuperSecretKey2025",
          },
        },
      );
      const data = await res.json();
      if (!data?.results?.items?.length) {
        sfx("no_deal");
        setResults({ error: "not_found", title: query });
        addLog("❌ GAME NOT FOUND IN DATABASE.", "error");
        setSuggestions(null);
        setCachedSearch(query, { items: [] }); // 💾 Cache empty result too
        fetch("https://scan4keys-backend.onrender.com/log", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "no_results", query }),
        }).catch(() => {});
        setSearchLoading(false);
        return;
      }
      setSuggestions(data.results.items);
      setCachedSearch(query, { items: data.results.items }); // 💾 Cache search results
      sfx("deal_found");
      addLog(`FOUND: ${data.results.items.length} MATCHES.`, "success");

      // Update URL without reload
      const newUrl = `/?q=${encodeURIComponent(query)}`;
      window.history.pushState({}, "", newUrl);
    } catch {
      sfx("no_deal");
      addLog("⚠️ API TIMEOUT.", "error");
    }
    setSearchLoading(false);
  };

  const clearSearch = () => {
    setQuery("");
    setSuggestions(null);
    setResults(null);
    setLogs([]);
    setShowSimilarGames(false);
    setShowHotDeals(false);

    // Clear similar games timeout
    if (similarGamesTimeoutRef.current) {
      clearTimeout(similarGamesTimeoutRef.current);
    }

    sfx("click");

    // Replay the hot deals transition after clear (with random delay 3.4s - 3.7s)
    const randomDelay = 3400 + Math.random() * 300;
    setTimeout(() => {
      const aside = document.querySelector("aside");
      if (aside) {
        aside.classList.add("animate-heavy-glitch");
        sfx("click");
        setTimeout(() => sfx("hover"), 200);
        setTimeout(() => sfx("typing"), 400);
        setTimeout(() => sfx("click"), 600);
      }

      setTimeout(() => {
        if (aside) {
          aside.classList.remove("animate-heavy-glitch");
        }
        setShowHotDeals(true);
        sfx("deal_found");
      }, 800);
    }, randomDelay);
  };

  const selectGame = async (id, title, image) => {
    setLastSelected({ id, title, image });
    setSuggestions(null);

    // Check cache first
    const cached = getCachedGame(id);
    if (cached) {
      addLog(
        `CACHE HIT: ${title} (prices from ${new Date(cached.timestamp).toLocaleTimeString()})`,
        "success",
      );
      if (!cached.thumb && image) {
        cached.thumb = image;
      }
      if (!cached.gameId && id) {
        cached.gameId = id;
      }
      setResults(cached);
      setLoading(false);
      sfx("deal_found");

      const newUrl = `/?id=${id}&title=${encodeURIComponent(title)}`;
      window.history.pushState({}, "", newUrl);
      return;
    }

    // Start loading animation immediately
    setLoading(true);
    setScanProgress(0);
    setScanError(false);
    setShowHotDeals(false);
    setShowSimilarGames(false);
    setShowAllOfficial(false);
    setShowAllKeySellers(false);
    setShowAllMarketplaces(false);

    setTimeout(() => {
      const realScrollContainer = document.querySelector(".min-h-screen");
      if (realScrollContainer) {
        realScrollContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    }, 50);

    if (similarGamesTimeoutRef.current) {
      clearTimeout(similarGamesTimeoutRef.current);
    }
    sfx("click");
    addLog(`ANALYZING: ${title} (fetching prices...)`, "process");

    // Start API call IMMEDIATELY (this is the ONLY wait)
    try {
      const res = await fetch(
        `https://scan4keys-backend.onrender.com/custom-prices?id=${id}`,
        {
          headers: {
            "x-client-key": "MySuperSecretKey2025",
          },
        },
      );

      if (!res) {
        setLoading(false);
        return;
      }
      // delay control (faster: first 1.4s, then 0.6-1.1s)
      let delay;

      if (!window._firstScanDone) {
        delay = 1100;
        window._firstScanDone = true;
      } else {
        delay = Math.floor(400 + Math.random() * 400);
      }

      await new Promise((resolve) => setTimeout(resolve, delay));

      const data = await res.json();
      console.log("🔍 FULL PRICE RESPONSE:", data);
      const available =
        data?.prices?.list?.filter((d) => d.available && d.price > 0) || [];

      if (!available.length) {
        setResults({ error: "none", title, thumb: image });
        sfx("no_deal");
        addLog("❌ NO DISCOUNTS DETECTED.", "error");
        setLoading(false);
      } else {
        const availableEditions = data.prices.editions || [];

        const pcDeals = available.filter((d) => {
          if (!d.edition_full) return false;
          return /FOR:(WINDOWS|MAC|LINUX)/i.test(d.edition_full);
        });

        const editionRetailPrices = {};
        const dealsByEdition = {};
        pcDeals.forEach((d) => {
          const editionKey = d.edition || "Standard";
          if (!dealsByEdition[editionKey]) dealsByEdition[editionKey] = [];
          dealsByEdition[editionKey].push(d);
        });

        Object.keys(dealsByEdition).forEach((editionKey) => {
          const editionDeals = dealsByEdition[editionKey];
          const officialPrices = [];
          const unofficialPrices = [];

          editionDeals.forEach((d) => {
            const isOfficial =
              d.store?.type === "Official Store" ||
              d.store?.official === true ||
              d.store?.name === "Steam" ||
              d.store?.name === "Epic Games Store" ||
              d.store?.name === "Microsoft Store";

            if (isOfficial) {
              officialPrices.push(d.price);
            } else {
              unofficialPrices.push(d.price);
            }
          });

          let retailPrice = null;

          if (officialPrices.length > 0) {
            const frequency = {};
            officialPrices.forEach((price) => {
              frequency[price] = (frequency[price] || 0) + 1;
            });

            let maxFreq = 0;
            let mostCommonPrice = null;
            Object.entries(frequency).forEach(([price, count]) => {
              if (count > maxFreq) {
                maxFreq = count;
                mostCommonPrice = parseFloat(price);
              }
            });

            if (maxFreq >= 3) {
              retailPrice = mostCommonPrice;
            }
          }

          if (retailPrice === null && officialPrices.length > 0) {
            retailPrice = Math.max(...officialPrices);
          }

          if (retailPrice === null) {
            retailPrice =
              data.prices.highest || Math.max(...unofficialPrices, 19.99);
          }

          editionRetailPrices[editionKey] = retailPrice;
        });

        const deals = pcDeals
          .map((d, index) => {
            let platform = "";
            let isMac = false;
            if (d.edition_full) {
              const match = d.edition_full.match(/FOR:(\w+)/);
              if (match) {
                platform = match[1];
                isMac = platform === "MAC";
              }
            }

            let storeType = "Official Store";
            if (d.store?.type === "Key Seller") storeType = "Key Seller";
            else if (d.store?.type === "Marketplace") storeType = "Marketplace";

            const editionKey = d.edition || "Standard";
            const retailPriceForEdition =
              editionRetailPrices[editionKey] || d.price;

            let calculatedOriginal = null;
            if (d.discount_percent && d.discount_percent > 0 && d.price > 0) {
              calculatedOriginal = d.price / (1 - d.discount_percent / 100);
            }

            const finalOriginal =
              calculatedOriginal || d.original_price || retailPriceForEdition;

            let finalUrl = d.url_raw || d.url;
            const storeName = d.store.name;

            if (storeName === "GameBillet") {
              const urlObj = new URL(finalUrl);
              urlObj.searchParams.set(
                "affiliate",
                "399f82a6-78af-434b-9913-a56fd62ef794",
              );
              finalUrl = urlObj.toString();
            }

            if (storeName === "GamersGate") {
              const urlObj = new URL(finalUrl);
              urlObj.searchParams.set(
                "aff",
                "fbec3cc265d855bcaa7b17e4a82b2a05350d20a4",
              );
              finalUrl = urlObj.toString();
            }

            if (storeName === "Green Man Gaming") {
              finalUrl = finalUrl.replace(
                "/c/1956250/1269775/",
                "/c/7134331/1219987/",
              );
            }

            if (storeName === "IndieGala") {
              finalUrl = finalUrl.replace(/ref=nexarda/, "ref=yjeynda");
            }

            if (storeName === "Instant Gaming") {
              const urlObj = new URL(finalUrl);
              urlObj.searchParams.set("igr", "scan4keys");
              finalUrl = urlObj.toString();
            }

            if (storeName === "YUPLAY") {
              const urlObj = new URL(finalUrl);
              urlObj.searchParams.set("partner", "3313564");
              finalUrl = urlObj.toString();
            }

            if (storeName === "Fanatical") {
              let decodedUrl = decodeURIComponent(finalUrl);
              const gameSlugMatch = decodedUrl.match(
                /fanatical\.com\/game\/([^?&]+)/,
              );
              if (gameSlugMatch && gameSlugMatch[1]) {
                const gameSlug = gameSlugMatch[1];
                finalUrl = `https://lt45.net/c/?si=13482&li=1594588&wi=419860&ws=&dl=game%2F${gameSlug}`;
              }
            }

            if (storeName === "Loaded") {
              let decodedUrl = decodeURIComponent(finalUrl);
              const gameSlugMatch = decodedUrl.match(/loaded\.com\/([^?&]+)/);
              if (gameSlugMatch && gameSlugMatch[1]) {
                const gameSlug = gameSlugMatch[1];
                finalUrl = `https://go.loaded.com/c/7134331/1566025/18216?u=https%3A%2F%2Fwww.loaded.com%2F${gameSlug}`;
              }
            }

            if (storeName === "Ubisoft Store") {
              let decodedUrl = decodeURIComponent(finalUrl);
              const gamePathMatch = decodedUrl.match(
                /store\.ubisoft\.com\/([^?]+\.html)/,
              );
              if (gamePathMatch && gamePathMatch[1]) {
                const gamePath = gamePathMatch[1];
                const encodedPath = encodeURIComponent(gamePath);
                finalUrl = `https://lt45.net/c/?si=14537&li=1625249&wi=419860&ws=&dl=${encodedPath}`;
              }
            }

            if (storeName === "JoyBuggy") {
              const urlObj = new URL(finalUrl);
              urlObj.searchParams.set("ref", "262");
              finalUrl = urlObj.toString();
            }

            return {
              store: d.store.name,
              storeID: d.store.id,
              storeImage: d.store.image,
              price: d.price,
              retail: retailPriceForEdition,
              originalPrice: finalOriginal,
              url: finalUrl,
              coupon: null,
              region: d.region || data.prices.regions?.[0] || "Global",
              edition:
                d.edition ||
                availableEditions[index % availableEditions.length] ||
                "Standard",
              platform: platform,
              isMac: isMac,
              storeType: storeType,
              storeOfficial: d.store?.official || false,
              discountPercent:
                finalOriginal > d.price
                  ? Math.round(
                      ((finalOriginal - d.price) / finalOriginal) * 100,
                    )
                  : 0,
            };
          })
          .sort((a, b) => a.price - b.price);

        const storeCounts = {
          official: deals.filter((d) => d.storeType === "Official Store")
            .length,
          keySellers: deals.filter((d) => d.storeType === "Key Seller").length,
          marketplaces: deals.filter((d) => d.storeType === "Marketplace")
            .length,
          total: deals.length,
        };

        const resultsData = {
          title,
          thumb: image,
          deals,
          gameId: id,
          editions: availableEditions,
          region: data.prices.regions?.[0] || "Global",
          storeCounts: storeCounts,
        };
        setGamePageKey((prev) => prev + 1);
        setResults(resultsData);
        setCachedGame(id, resultsData);
        setShowHotDeals(false);
        sfx("deal_found");
        addLog("DEAL REPORT GENERATED.", "success");
        const officialDealsLog = deals.filter(
          (d) => d.storeType === "Official Store",
        );
        const keySellerDealsLog = deals.filter(
          (d) => d.storeType === "Key Seller",
        );
        fetch("https://scan4keys-backend.onrender.com/log", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "scan_complete",
            game: title,
            officialPrice: officialDealsLog[0]?.price || null,
            officialCount: officialDealsLog.length,
            keySellerPrice: keySellerDealsLog[0]?.price || null,
            keySellerCount: keySellerDealsLog.length,
          }),
        }).catch(() => {});

        const newUrl = `/?id=${id}&title=${encodeURIComponent(title)}`;
        window.history.pushState({}, "", newUrl);
      }
    } catch {
      addLog("⚠️ API TIMEOUT.", "error");
      setScanGlitching(false);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-green-400 font-mono relative selection:bg-green-900 overflow-y-auto custom-scrollbar h-screen crt-wrapper overflow-x-hidden">
      <canvas
        ref={canvasRef}
        className="fixed inset-0 z-0 pointer-events-none opacity-67"
      />
      <div className="fixed inset-0 z-[61] scanlines pointer-events-none" />
      <div className="fixed inset-0 z-[62] crt-rolling-wave pointer-events-none" />
      <div className="fixed inset-0 z-[63] vignette pointer-events-none" />
      <div className="fixed inset-0 z-[64] noise-overlay pointer-events-none opacity-5" />
      <div className="fixed inset-0 z-[65] pointer-events-none vhs-tracking-jitter" />

      {/* Exposure Layer */}
      <div className="exposure-layer" />

      {/* Subtle Green Tint Layer */}
      <div className="green-tint" />

      <div
        className="max-w-7xl mx-auto border-x border-green-500/40 bg-black/95 flex flex-col min-h-screen relative z-10 shadow-[0_0_80px_rgba(0,0,0,1),0_0_20px_rgba(34,197,94,0.15)] crt-content overflow-x-hidden"
        style={{
          zoom: (() => {
            if (typeof window !== "undefined") {
              // Detect Edge browser
              const isEdge = /Edg/.test(navigator.userAgent);
              if (isEdge) return 0.82; // Slight zoom for Edge
              return window.innerWidth < 768 ? 0.95 : 1.04;
            }
            return 1.04;
          })(),
        }}
      >
        {activeRedirect && (
          <div className="fixed inset-0 z-[100] bg-black/98 flex items-center justify-center p-2 md:p-6 backdrop-blur-md">
            <div className="relative w-full max-w-5xl bg-black border-2 border-green-600/80 flex flex-col animate-in fade-in zoom-in-95 rounded-lg shadow-[0_0_40px_rgba(34,197,94,0.3)]">
              {/* HEADER - Store Logo + Name + Prominent Badge */}
              <div className="bg-gradient-to-r from-green-950/80 to-black p-4 border-b border-green-500/50 rounded-t-lg">
                {/* X Button - Top Right Absolute */}
                <div className="relative">
                  <button
                    onClick={() => {
                      sfx("click");
                      setActiveRedirect(null);
                    }}
                    className="absolute top-0 right-0 hover:bg-white/10 p-1.5 rounded-full transition-all z-20"
                  >
                    <X size={24} className="text-green-500" />
                  </button>
                </div>

                {/* Main Content */}
                <div className="flex items-center gap-4 pr-8">
                  {activeRedirect.storeImage && (
                    <img
                      src={activeRedirect.storeImage}
                      alt={activeRedirect.store}
                      className="w-12 h-12 object-contain rounded-lg bg-black/50 p-1 border border-green-500/50 flex-shrink-0"
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  )}
                  <div>
                    <h2 className="text-sm sm:text-xl md:text-2xl font-black uppercase tracking-wider text-white">
                      {activeRedirect.store}
                    </h2>

                    {/* PROMINENT SAFETY BADGE */}
                    {activeRedirect.storeType === "Official Store" && (
                      <div className="mt-1 inline-flex items-center gap-2 bg-green-600 px-3 py-1 rounded-full shadow-[0_0_12px_rgba(34,197,94,0.6)]">
                        <ShieldCheck size={14} className="text-black" />
                        <span className="text-black font-black text-xs uppercase tracking-wide">
                          AUTHORIZED & 100% SAFE
                        </span>
                      </div>
                    )}
                    {activeRedirect.storeType === "Key Seller" && (
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        {/* INFO BADGE - Light Purple */}
                        <div className="inline-flex items-center gap-2 bg-purple-400 px-3 py-1 rounded-full shadow-[0_0_12px_rgba(168,85,247,0.5)]">
                          <Info size={14} className="text-black" />
                          <span className="text-black font-black text-[10px] uppercase tracking-wide">
                            UNOFFICIAL SELLER
                          </span>
                        </div>

                        {/* DYNAMIC REPUTATION BADGE - Updated colors */}
                        {(() => {
                          const storeName = activeRedirect.store?.toLowerCase();
                          let repText = "MIXED REPUTATION";
                          let repStyle =
                            "bg-yellow-600 text-black shadow-[0_0_12px_rgba(234,179,8,0.5)]";
                          let Icon = AlertTriangle;

                          if (
                            storeName === "loaded" ||
                            storeName === "yuplay" ||
                            storeName === "instant gaming"
                          ) {
                            repText = "GOOD REPUTATION";
                            repStyle =
                              "bg-lime-500 text-black shadow-[0_0_12px_rgba(163,230,53,0.5)]";
                            Icon = CheckCircle2;
                          }

                          return (
                            <div
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${repStyle}`}
                            >
                              <Icon size={12} className="text-black" />
                              {repText}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {activeRedirect.storeType === "Marketplace" && (
                      <div className="mt-1 inline-flex items-center gap-2 bg-red-600 px-3 py-1 rounded-full shadow-[0_0_12px_rgba(220,38,38,0.5)]">
                        <AlertTriangle size={14} className="text-black" />
                        <span className="text-black font-black text-xs uppercase tracking-wide">
                          MARKETPLACE – PROCEED WITH CAUTION
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* BETA NOTICE + TWO BOXES SIDE BY SIDE */}
              <div className="p-6 pb-0">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Beta Notice */}
                  <div className="flex-1 bg-yellow-950/40 border-l-8 border-yellow-500 p-3.5 rounded-r-xl shadow-[0_0_20px_rgba(234,179,8,0.2)]">
                    <div className="flex items-start gap-2.5">
                      <AlertTriangle
                        size={22}
                        className="text-yellow-500 flex-shrink-0 mt-0.5 hidden sm:block"
                      />
                      <div className="flex-1">
                        <p className="text-yellow-400 font-black text-xs sm:text-sm uppercase tracking-wide mb-1.5">
                          ⚠️ BETA NOTICE – DOUBLE CHECK EVERYTHING
                        </p>

                        {/* Mobile: Swipeable Carousel */}
                        <div
                          id="beta-carousel"
                          className="block sm:hidden overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                        >
                          <div className="flex gap-4 pb-2">
                            {[
                              "✓ Our tool is usually accurate, but occasional glitches happen",
                              "✓ Prices default to USD – your region & currency may differ",
                              "✓ ALWAYS verify final price, region, and edition on the store page",
                            ].map((tip, idx) => (
                              <div
                                key={idx}
                                className="snap-center flex-shrink-0 w-full bg-yellow-900/30 p-2 rounded"
                              >
                                <p className="text-yellow-300 text-[10px] leading-relaxed">
                                  {tip}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div
                            id="beta-dots"
                            className="flex justify-center gap-1 mt-2"
                          >
                            {[0, 1, 2].map((idx) => (
                              <div
                                key={idx}
                                className="w-1.5 h-1.5 rounded-full bg-yellow-500/50"
                              ></div>
                            ))}
                          </div>
                        </div>

                        {/* Desktop: Regular */}
                        <div className="hidden sm:block text-yellow-300 text-sm leading-relaxed space-y-1">
                          <p>
                            ✓ Our tool is usually accurate, but occasional
                            glitches happen
                          </p>
                          <p>
                            ✓ Prices default to USD – your region & currency may
                            differ
                          </p>
                          <p>
                            ✓{" "}
                            <span className="font-bold">
                              ALWAYS verify final price, region, and edition on
                              the store page
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Side Boxes - Stacked */}
                  <div className="sm:w-auto flex flex-col gap-2">
                    {/* Red Box - Adblock Warning */}
                    <div className="bg-red-950/40 border-2 border-red-500 rounded-lg p-2 shadow-[0_0_15px_rgba(220,38,38,0.3)]">
                      <div className="flex items-center justify-center gap-2">
                        <AlertTriangle
                          size={12.5}
                          className="text-red-400 flex-shrink-0"
                        />
                        <span className="text-red-400 font-black text-[8.5px] sm:text-[9.5px] uppercase tracking-wider whitespace-nowrap">
                          If you're using an adblocker, it may block the
                          redirect
                        </span>
                        <AlertTriangle
                          size={12.5}
                          className="text-red-400 flex-shrink-0"
                        />
                      </div>
                    </div>

                    {/* Purple Box - Report Errors */}
                    <div className="bg-purple-950/40 border-2 border-purple-500 rounded-lg p-2 shadow-[0_0_15px_rgba(147,51,234,0.3)]">
                      <div className="flex items-center justify-center gap-2">
                        <Mail
                          size={12.5}
                          className="text-purple-400 flex-shrink-0"
                        />
                        <span className="text-purple-400 font-black text-[8.5px] sm:text-[9.5px] uppercase tracking-wider whitespace-nowrap">
                          See an error? Report to contact@scan4keys.com
                        </span>
                        <Mail
                          size={12.5}
                          className="text-purple-400 flex-shrink-0"
                        />
                      </div>
                    </div>
                    {/* Lime Box - Thank You */}
                    <div className="bg-lime-950/40 border-2 border-lime-500 rounded-lg p-2 shadow-[0_0_15px_rgba(132,204,22,0.3)]">
                      <div className="flex items-center justify-center gap-2">
                        <Heart
                          size={12.5}
                          className="text-lime-400 flex-shrink-0"
                        />
                        <span className="text-lime-400 font-black text-[8.5px] sm:text-[9.5px] uppercase tracking-wider whitespace-nowrap">
                          Thank you for using Scan4Keys, Enjoy the savings!
                        </span>
                        <Heart
                          size={12.5}
                          className="text-lime-400 flex-shrink-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* MAIN CONTENT - 2 COLUMN LAYOUT */}
              <div className="flex flex-col md:flex-row gap-6 p-6 pt-4">
                {/* LEFT COLUMN - Tutorial Video */}
                <div className="hidden sm:flex flex-1 flex-col space-y-3">
                  <div className="flex items-center gap-2 text-[14px] font-black uppercase text-green-500 tracking-wider">
                    <Monitor size={17} />
                    <span>How to redeem codes on Steam</span>
                  </div>
                  <div className="aspect-video w-full bg-black border border-green-800 rounded-lg overflow-hidden">
                    <iframe
                      className="w-full h-full hover:grayscale-0 transition-all duration-700"
                      src={`${tutorialMap[activeRedirect.storeID] || tutorialMap.default}?autoplay=0&rel=0`}
                      allowFullScreen
                      title="Store Tutorial"
                    ></iframe>
                  </div>
                </div>

                {/* RIGHT COLUMN - Deal Details & Action */}
                <div className="flex-1 space-y-5">
                  {/* DEAL DETAILS GRID - With Game Title full width */}
                  <div className="divide-y divide-green-800/40 border border-green-800/50 rounded-xl overflow-hidden bg-black/40">
                    {/* Row 1: Game Title - Full Width */}
                    <div className="p-4">
                      <p className="text-[9px] font-bold text-green-600 uppercase tracking-wider">
                        Game Title
                      </p>
                      <p className="text-white font-black text-sm md:text-base mt-0.5 leading-tight">
                        {results?.title || "Game"}
                      </p>
                    </div>
                    {/* Row 2: Edition & Platform */}
                    <div className="grid grid-cols-2 gap-3 p-4">
                      <div>
                        <p className="text-[9px] font-bold text-green-600 uppercase tracking-wider">
                          Edition
                        </p>
                        <p className="text-white font-black text-sm md:text-base mt-0.5">
                          {activeRedirect.edition || "Standard"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] font-bold text-green-600 uppercase tracking-wider">
                          Platform
                        </p>
                        <p className="text-white font-black text-sm md:text-base mt-0.5">
                          {activeRedirect.platform || "PC"}
                        </p>
                      </div>
                    </div>
                    {/* Row 3: Region & Price */}
                    <div className="grid grid-cols-2 gap-3 p-4">
                      <div>
                        <p className="text-[9px] font-bold text-green-600 uppercase tracking-wider">
                          Region
                        </p>
                        <p className="text-white font-black text-sm md:text-base mt-0.5">
                          Check on Store
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] font-bold text-green-600 uppercase tracking-wider">
                          Price
                        </p>
                        <div className="flex items-baseline gap-1.5 flex-wrap mt-0.5">
                          <span className="text-green-400 font-black text-xl">
                            ${activeRedirect.price?.toFixed(2)}
                          </span>
                          {activeRedirect.retail &&
                            activeRedirect.retail > activeRedirect.price && (
                              <>
                                <span className="text-green-700 line-through text-xs">
                                  ${activeRedirect.retail?.toFixed(2)}
                                </span>
                                <span className="text-[9px] font-bold text-green-400 bg-green-950/60 px-1 py-0.5 rounded">
                                  -
                                  {Math.round(
                                    ((activeRedirect.retail -
                                      activeRedirect.price) /
                                      activeRedirect.retail) *
                                      100,
                                  )}
                                  %
                                </span>
                              </>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* REDIRECT BUTTON */}
                  {(() => {
                    const hasCoupon =
                      activeRedirect.coupon &&
                      activeRedirect.coupon.available &&
                      activeRedirect.coupon.code;

                    if (hasCoupon) {
                      return (
                        <div className="space-y-3">
                          <button
                            onClick={() => {
                              sfx("click");
                              setPendingRedirectDeal(activeRedirect);
                              setShowCouponModal(true);
                            }}
                            onMouseEnter={() => sfx("hover")}
                            className="w-full bg-gradient-to-r from-green-600 to-green-500 text-black py-4 px-4 font-black text-base text-center hover:from-green-500 hover:to-green-400 transition-all rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.5)] uppercase block relative group overflow-hidden"
                          >
                            <div className="absolute inset-0 bg-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                            <span className="flex items-center justify-center gap-2 relative z-10">
                              GO TO STORE <ExternalLink size={18} />
                            </span>
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        onClick={() => {
                          sfx("click");
                          // Send log to backend before redirect
                          fetch("https://scan4keys-backend.onrender.com/log", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              type: "redirect",
                              store: activeRedirect.store,
                              game: results?.title,
                              price: activeRedirect.price,
                            }),
                          }).catch((err) => console.log("Log failed:", err));

                          // Check if deal has a coupon
                          if (
                            activeRedirect.coupon &&
                            activeRedirect.coupon.available &&
                            activeRedirect.coupon.code
                          ) {
                            // Show coupon modal first, pass the deal data
                            setPendingRedirectDeal(activeRedirect);
                            setShowCouponModal(true);
                          } else {
                            // No coupon, open store directly
                            window.open(activeRedirect.url, "_blank");
                            setActiveRedirect(null);
                          }
                        }}
                        onMouseEnter={() => sfx("hover")}
                        className="w-full bg-gradient-to-r from-green-600 to-green-500 text-black py-4 px-4 font-black text-base text-center hover:from-green-500 hover:to-green-400 transition-all rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.5)] uppercase block relative group overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                        <span className="flex items-center justify-center gap-2 relative z-10">
                          GO TO STORE <ExternalLink size={18} />
                        </span>
                      </button>
                    );
                  })()}

                  {/* EXTRA VERIFICATION NOTE */}
                  <p className="text-center text-[8px] text-green-800 uppercase tracking-wider">
                    You will be redirected to {activeRedirect.store}. Verify
                    price before purchasing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {isAlertModalOpen && (
          <div className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-black border-2 border-green-500 p-8 max-w-xl w-full relative rounded-xl shadow-[0_0_100px_rgba(34,197,94,0.2)] animate-in zoom-in-95">
              {/* COMING SOON OVERLAY - Clean & Filled */}
              <div className="absolute inset-0 z-10 bg-black/95 backdrop-blur-md rounded-xl flex flex-col items-center justify-center gap-5 animate-in fade-in duration-300 p-6">
                {/* Badge */}
                <div className="inline-block bg-yellow-500/20 border border-yellow-500 rounded-full px-4 py-1.5">
                  <span className="text-yellow-400 text-[11px] font-black uppercase tracking-wider">
                    🚧 UNDER CONSTRUCTION 🚧
                  </span>
                </div>

                {/* Title */}
                <div className="text-center">
                  <p className="text-white font-black text-3xl md:text-4xl uppercase tracking-wide">
                    PRICE ALERT
                  </p>
                  <p className="text-yellow-400 font-black text-2xl md:text-3xl mt-1">
                    COMING SOON
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px w-40 bg-gradient-to-r from-transparent via-yellow-500 to-transparent" />

                {/* Main Message - Single clear call to action */}
                <div className="text-center space-y-2 max-w-sm">
                  <p className="text-green-400 font-bold text-base uppercase tracking-wide">
                    We're still building this feature.
                  </p>
                  <p className="text-yellow-400 font-black text-lg uppercase tracking-wide leading-tight"></p>
                </div>

                {/* Action Reminder Box */}
                <div className="mt-2 w-full bg-yellow-950/40 border-2 border-yellow-500/70 rounded-lg p-4 text-center">
                  <p className="text-yellow-400 font-black text-sm uppercase tracking-wide">
                    Check back every once in a while to see if it's ready
                  </p>
                  <p className="text-yellow-500 text-xs font-mono mt-1"></p>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setIsAlertModalOpen(false)}
                  onMouseEnter={() => sfx("hover")}
                  className="mt-2 bg-green-600 text-black font-black py-3 px-8 uppercase text-sm hover:bg-green-500 transition-all rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.4)] w-full max-w-xs"
                >
                  Close
                </button>
              </div>

              {/* ORIGINAL MODAL CONTENT - Fully dimmed, preserved for future */}
              <div className="opacity-0 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-1 bg-green-500/20" />
                <div className="flex items-center gap-4 border-b border-green-700 pb-6">
                  <Bell size={32} className="text-green-400" />
                  <div>
                    <h2 className="text-2xl font-black uppercase tracking-tight">
                      Price Alert
                    </h2>
                    <p className="text-[10px] font-bold text-green-700 uppercase tracking-widest">
                      Stay updated on cost changes
                    </p>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-green-950/20 p-4 border border-green-700 rounded">
                    <p className="text-xs uppercase text-green-400 font-bold mb-1">
                      Target Game
                    </p>
                    <p className="text-lg font-black text-white uppercase italic">
                      {results?.title}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <p className="text-sm text-green-500 font-medium leading-relaxed">
                      Enter your email and we’ll monitor this game across all
                      stores for you. As soon as a price drop is detected,
                      you’ll get notified instantly.
                    </p>
                    <div className="relative">
                      <Mail
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-green-800"
                        size={20}
                      />
                      <input
                        type="email"
                        placeholder="YOUR EMAIL ADDRESS"
                        className="w-full bg-black border-2 border-green-800 p-5 pl-12 text-green-400 outline-none uppercase font-black focus:border-green-400 transition-all rounded"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    className="flex-1 bg-green-600 text-black font-black py-4 uppercase text-lg rounded shadow-[0_0_20px_rgba(34,197,94,0.2)] cursor-not-allowed"
                    disabled
                  >
                    Set Alert
                  </button>
                  <button
                    onClick={() => setIsAlertModalOpen(false)}
                    className="flex-1 border-2 border-green-700 py-4 text-green-800 uppercase font-black rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <header className="border-b border-green-900/40 p-4 flex justify-between items-center bg-black/90 backdrop-blur-md z-40 sticky top-0 shadow-[0_0_40px_rgba(34,197,94,0.1)] overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-green-500/30 animate-scanline-fast" />
          <div className="flex items-center gap-4 relative z-10">
            <div
              onClick={() => {
                resetApp();
                // Reset URL to homepage without reload
                window.history.pushState({}, "", "/");
                // Reset share execution flag
                shareExecutedRef.current = false;
              }}
            >
              <CyberLogo />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-sm md:text-xl font-black uppercase tracking-[0.3em] leading-none drop-shadow-[0_0_15px_rgba(34,197,94,0.8)] animate-title-flicker">
                  SCAN 4{" "}
                  <span className="text-white relative">
                    KEYS{" "}
                    <span className="absolute -inset-x-2 -bottom-1 h-[2px] bg-green-500 animate-pulse" />
                  </span>
                </h1>
                <span className="hidden sm:inline-block bg-red-600 text-black px-2 py-0.5 text-[10px] font-black rounded animate-pulse shadow-[0_0_10px_rgba(220,38,38,0.5)]">
                  BETA v0.4.2
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[9px] text-green-800 font-black uppercase tracking-[0.1em] flex items-center gap-1">
                  <div className="w-1 h-1 bg-green-500 rounded-full animate-ping" />{" "}
                  SECURE_NODE: v6.7.2
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 relative z-10">
            <div className="hidden md:flex items-center gap-3 px-3 py-1 bg-green-950/20 border border-green-500/40 shadow-[0_0_10px_rgba(34,197,94,0.08)] rounded text-[10px] font-black uppercase tracking-widest text-green-700">
              <Activity size={10} className="animate-pulse" /> ENCRYPTION:
              AES-256
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                onMouseEnter={() => sfx("hover")}
                className={`p-2 transition-all duration-300 rounded hover:bg-white/5 ${soundEnabled ? "text-green-400" : "text-green-900"}`}
              >
                {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
              </button>
              <button
                onClick={() => setShowLegalModal(true)}
                onMouseEnter={() => sfx("hover")}
                className="p-2 text-green-700 hover:text-green-400 transition-colors"
              >
                <Info size={20} />
              </button>
            </div>
          </div>
        </header>

        {announcement && (
          <div className="relative overflow-hidden border-b border-fuchsia-500/40 bg-gradient-to-r from-purple-950/60 via-black to-purple-950/60 px-7 py-4 flex items-center justify-between shadow-[0_0_40px_rgba(217,70,239,0.25)]">
            {/* Mobile: JavaScript Infinite Marquee - Small Gap */}
            <div className="block sm:hidden overflow-hidden flex-1">
              <div
                id="marquee-container"
                className="whitespace-nowrap text-fuchsia-300 font-black uppercase tracking-widest text-xs"
                style={{ display: "inline-block" }}
              >
                <span className="animate-pulse inline-block mr-2">
                  👾 COMING SOON 👾
                </span>
                {announcement.text}
                <span className="inline-block mx-8"></span>
              </div>
            </div>
            {/* Desktop: Normal (unchanged) */}
            <div className="hidden sm:flex items-center gap-4 text-fuchsia-300 font-black uppercase tracking-widest text-xs md:text-sm relative z-10">
              <span className="animate-pulse">👾 COMING SOON 👾</span>
              <span className="text-white">{announcement.text}</span>
            </div>

            <button
              onClick={() => setAnnouncement(null)}
              onMouseEnter={() => sfx("hover")}
              className="hidden sm:inline-block text-fuchsia-400 hover:text-white text-xs font-bold tracking-widest transition-all hover:scale-110"
            >
              DISMISS
            </button>
          </div>
        )}

        <div className="p-4 md:p-5 space-y-6 flex-1 flex flex-col relative">
          {/* Subtle Corner Accents - Brighter */}
          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-green-600/40 pointer-events-none" />
          <div
            className="absolute right-2 w-4 h-4 border-t-2 border-r-2 border-green-600/40 pointer-events-none"
            style={{ top: "-0.9rem" }}
          />
          <form
            onSubmit={handleSearch}
            className="relative group animate-in slide-in-from-top-4 duration-700"
            style={{ marginTop: "0.5rem" }}
          >
            <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none z-10">
              <Search
                className={`w-6 h-6 transition-all duration-500 ${searchLoading ? "text-white animate-spin" : "text-green-700 group-hover:text-green-400 group-hover:scale-110"}`}
              />
            </div>

            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                sfx("typing");
              }}
              placeholder="ENTER GAME TITLE (e.g. HITMAN)..."
              className="w-full bg-black/60 border-2 border-green-900/60 p-6 pl-16 pr-12 text-green-400 focus:border-green-500 focus:bg-black/90 outline-none uppercase text-lg font-bold tracking-[0.2em] rounded-lg transition-all hover:shadow-[0_0_30px_rgba(34,197,94,0.15)] relative z-0"
            />

            {query && (
              <button
                type="button"
                onClick={clearSearch}
                onMouseEnter={() => sfx("hover")}
                className="absolute inset-y-0 right-4 z-20 flex items-center text-green-700 hover:text-red-500 transition-all hover:rotate-90"
              >
                <X size={20} />
              </button>
            )}
          </form>

          <div className="flex flex-col lg:grid lg:grid-cols-4 gap-8 flex-1 min-h-0">
            <aside
              className={`lg:col-span-1 border ${showHotDeals ? "border-purple-500/30 shadow-[0_0_10px_rgba(147,51,234,0.08)] shadow-[0_0_15px_rgba(147,51,234,0.05)]" : "border-green-500/40 shadow-[0_0_10px_rgba(34,197,94,0.08)] shadow-[0_0_15px_rgba(34,197,94,0.05)]"} p-4 bg-black/60 min-h-[200px] lg:min-h-[400px] lg:h-auto flex flex-col overflow-hidden rounded-lg relative order-2 lg:order-1 group animate-in slide-in-from-left-4 duration-700 transition-all duration-500 ease-in-out ""`}
            >
              <div className="absolute inset-0 bg-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

              {showSimilarGames ? (
                // SIMILAR GAMES SECTION - VERTICAL COVERS (with glitch)
                <div
                  className={`flex flex-col h-full ${similarGamesGlitch ? "animate-heavy-glitch" : ""}`}
                >
                  <div className="text-[10px] font-black bg-blue-950/20 p-2 mb-3 text-center uppercase text-blue-400/70 border-b border-blue-500/30 tracking-[0.2em] rounded">
                    YOU MIGHT ALSO LIKE
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-3 pr-1 custom-scrollbar">
                    {similarGames
                      .slice(0, visibleSimilarCount)
                      .map((game, idx) => (
                        <button
                          key={game.game_info?.id || idx}
                          onClick={() => {
                            // Use the existing game data from similarGames, don't fetch again!
                            const gameData = game;
                            if (gameData && gameData.game_info?.id) {
                              console.log(
                                "✅ SELECTING GAME:",
                                gameData.title,
                                gameData.game_info.id,
                              );
                              selectGame(
                                gameData.game_info.id,
                                gameData.title,
                                gameData.image,
                              );
                            } else if (gameData && gameData.id) {
                              console.log(
                                "✅ SELECTING GAME (fallback id):",
                                gameData.title,
                                gameData.id,
                              );
                              selectGame(
                                gameData.id,
                                gameData.title,
                                gameData.image,
                              );
                            } else {
                              console.log(
                                "❌ NO ID FOUND, falling back to search",
                              );
                              setQuery(game.title);
                              setTimeout(
                                () =>
                                  handleSearch({ preventDefault: () => {} }),
                                100,
                              );
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            const gameId = game.game_info?.id || game.id;
                            const gameTitle = game.title;
                            const gameImage = game.image;
                            setContextMenu({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              url: `/?id=${gameId}&title=${encodeURIComponent(gameTitle)}`,
                              gameTitle: gameTitle,
                              gameId: gameId,
                              gameImage: gameImage,
                            });
                          }}
                          onMouseEnter={() => sfx("hover")}
                          className="w-full flex flex-col items-center gap-2 p-3 bg-black/30 border border-blue-500/20 rounded hover:border-blue-400/50 hover:bg-blue-950/10 transition-all duration-300 group"
                        >
                          {game.image && (
                            <img
                              src={game.image}
                              alt=""
                              className="w-full object-cover rounded opacity-40 group-hover:opacity-100 transition-all"
                              style={{
                                aspectRatio: "6/7",
                                maxHeight: "300px",
                                width: "100%",
                              }}
                            />
                          )}
                          <span className="text-[10px] font-black text-blue-400/70 uppercase text-center truncate w-full">
                            {game.title}
                          </span>
                          <ChevronRight
                            size={14}
                            className="text-blue-600/50 group-hover:text-blue-400/70"
                          />
                        </button>
                      ))}
                  </div>

                  {similarGames.length > 4 && !showAllSimilar && (
                    <button
                      onClick={() => setShowAllSimilar(true)}
                      onMouseEnter={() => sfx("hover")}
                      className="mt-2 w-full bg-blue-600/20 border border-blue-500/30 text-blue-400/70 font-black py-2 text-[9px] uppercase tracking-wider rounded-md hover:bg-blue-600/30 transition-all duration-300"
                    >
                      SHOW MORE ▼ ({similarGames.length - 4} more)
                    </button>
                  )}

                  {showAllSimilar && similarGames.length > 4 && (
                    <button
                      onClick={() => setShowAllSimilar(false)}
                      onMouseEnter={() => sfx("hover")}
                      className="mt-2 w-full bg-gradient-to-r from-gray-600 to-gray-500 text-white font-black py-2 text-[9px] uppercase tracking-wider rounded-md transition-all duration-300"
                    >
                      SHOW LESS ▲
                    </button>
                  )}

                  <div className="mt-2 pt-2 border-t border-blue-500/20 text-[7px] font-black uppercase text-blue-500/50 tracking-widest text-center">
                    {similarGames.length} SIMILAR GAMES FOUND
                  </div>
                </div>
              ) : !showHotDeals ? (
                // ORIGINAL ACTIVITY LOG
                <>
                  <div className="text-[10px] font-black bg-green-900/20 p-2 mb-2 text-center uppercase text-green-600 border-b border-green-600/40 tracking-[0.4em]">
                    Activity Log
                  </div>
                  <div
                    className="flex-1 overflow-y-auto space-y-1 text-[10px] font-bold pr-2 custom-scrollbar"
                    ref={scrollRef}
                  >
                    {logs.map((l) => (
                      <div
                        key={l.id}
                        className={`${l.type === "error" ? "text-red-500" : l.type === "process" ? "text-yellow-500" : "text-green-600"} border-l-2 border-current pl-2 transition-all duration-300 hover:bg-white/5`}
                      >{`> ${l.text}`}</div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-green-900/20 text-[8px] font-black uppercase text-green-900 tracking-widest text-center animate-pulse">
                    System Active // No Breach
                  </div>
                </>
              ) : (
                // HOT DEALS SECTION - HOLOGRAPHIC PURPLE THEME
                <div className="flex flex-col h-full rounded-lg p-1 relative overflow-hidden">
                  {/* Holographic background effect - MORE VISIBLE */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-800/20 via-fuchsia-700/15 to-purple-800/20" />
                  <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_0%,rgba(147,51,234,0.15)_50%,transparent_100%)] animate-[shimmer_3s_infinite]" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(168,85,247,0.15),transparent_60%)]" />
                  <div className="relative z-10 flex flex-col h-full">
                    {/* Rotating Title with Glitch */}
                    <div
                      className={`bg-purple-950/40 p-2 mb-3 text-center border-b-2 border-purple-400/60 rounded shadow-[0_0_15px_rgba(147,51,234,0.3)] ${titleGlitch ? "animate-rgb-split" : ""}`}
                    >
                      <div className="flex items-center justify-center gap-2 font-black text-[10px] sm:text-[11.7px] uppercase tracking-[0.2em]">
                        <Trophy size={14} className="text-purple-400/70" />
                        <span className="text-amber-400 drop-shadow-[0_0_6px_rgba(245,158,11,0.7)]">
                          10/10
                        </span>
                        <span className="text-purple-300 drop-shadow-[0_0_8px_rgba(168,85,247,0.5)]">
                          QUALITY ON SALE
                        </span>
                        <Tag size={14} className="text-purple-400/70" />
                      </div>
                    </div>

                    {/* 2x2 Grid - Clean & Simple */}
                    <div className="grid grid-cols-2 gap-1 flex-1">
                      {hotDealsList.map((game) => (
                        <button
                          key={game.id}
                          onClick={() => {
                            selectGame(game.id, game.title, game.image);
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setContextMenu({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              url: `/?id=${game.id}&title=${encodeURIComponent(game.title)}`,
                              gameTitle: game.title,
                              gameId: game.id,
                              gameImage: game.image,
                            });
                          }}
                          onMouseEnter={() => sfx("hover")}
                          className="group relative rounded-lg overflow-hidden border border-fuchsia-500/30 hover:border-fuchsia-400 transition-all duration-300 hover:scale-[1.02] bg-black/60"
                          style={{ aspectRatio: "2/3" }}
                        >
                          <img
                            src={game.image}
                            alt=""
                            className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                            onError={(e) => {
                              e.target.src =
                                "https://placehold.co/200x300/0a0f0a/fuchsia?text=No+Image";
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
                          <div className="absolute top-2 right-2 bg-black/80 border border-purple-500 text-purple-300 text-[11px] font-mono font-black px-1.5 py-0.5 shadow-[0_0_10px_rgba(147,51,234,0.4)] tracking-wider">
                            -{game.discount}%
                          </div>
                          <div className="absolute bottom-2 left-2 right-2 text-center">
                            <div className="flex items-baseline justify-center gap-2">
                              <span className="text-fuchsia-300 font-black text-base md:text-lg drop-shadow-[0_0_10px_rgba(217,70,239,0.6)]">
                                ${game.price.toFixed(2)}
                              </span>
                              <span className="text-green-700 line-through text-[10px]">
                                $
                                {(
                                  game.price /
                                  (1 - game.discount / 100)
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Buttons - Expand All Style */}
                    <div className="mt-3 space-y-2">
                      <button
                        onClick={() => {
                          sfx("click");
                          fetch("https://scan4keys-backend.onrender.com/log", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              type: "hot_deals_click",
                              store: "BROWSE MORE",
                            }),
                          }).catch(() => {});
                          setResults({
                            error: "coming_soon",
                            title: "Feature Coming Soon",
                          });
                        }}
                        onMouseEnter={() => sfx("hover")}
                        className="group relative overflow-hidden w-full bg-purple-600/20 border-2 border-purple-500 rounded-md hover:bg-purple-600 hover:border-purple-400 transition-all duration-300 shadow-[0_0_15px_rgba(147,51,234,0.3)] hover:shadow-[0_0_25px_rgba(147,51,234,0.6)]"
                      >
                        <span className="relative z-10 text-purple-400 group-hover:text-black font-black text-[13px] sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 py-2.5">
                          <ChevronDown size={16} /> BROWSE MORE
                        </span>
                        <div className="absolute inset-0 bg-purple-500 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                      </button>

                      <button
                        onClick={() => {
                          sfx("click");
                          fetch("https://scan4keys-backend.onrender.com/log", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              type: "hot_deals_click",
                              store: "POPULAR GAMES",
                            }),
                          }).catch(() => {});
                          setResults({
                            error: "coming_soon",
                            title: "Feature Coming Soon",
                          });
                        }}
                        onMouseEnter={() => sfx("hover")}
                        className="group relative overflow-hidden w-full bg-amber-600/20 border-2 border-amber-500 rounded-md hover:bg-amber-600 hover:border-amber-400 transition-all duration-300 shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.6)]"
                      >
                        <span className="relative z-10 text-amber-400 group-hover:text-black font-black text-[13px] sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 py-2.5">
                          <Flame size={16} /> POPULAR GAMES
                        </span>
                        <div className="absolute inset-0 bg-amber-500 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                      </button>

                      <button
                        onClick={() => {
                          sfx("click");
                          fetch("https://scan4keys-backend.onrender.com/log", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              type: "hot_deals_click",
                              store: "NEW RELEASES",
                            }),
                          }).catch(() => {});
                          setResults({
                            error: "coming_soon",
                            title: "Feature Coming Soon",
                          });
                        }}
                        onMouseEnter={() => sfx("hover")}
                        className="group relative overflow-hidden w-full bg-cyan-600/20 border-2 border-cyan-500 rounded-md hover:bg-cyan-600 hover:border-cyan-400 transition-all duration-300 shadow-[0_0_15px_rgba(6,182,212,0.3)] hover:shadow-[0_0_25px_rgba(6,182,212,0.6)]"
                      >
                        <span className="relative z-10 text-cyan-400 group-hover:text-black font-black text-[13px] sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 py-2.5">
                          <Rocket size={16} /> NEW RELEASES
                        </span>
                        <div className="absolute inset-0 bg-cyan-500 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                      </button>
                    </div>

                    <div className="mt-2 pt-2 border-t border-fuchsia-500/30 text-[7px] font-black uppercase text-fuchsia-600/80 tracking-widest text-center">
                      4 DEALS READY
                    </div>
                  </div>
                </div>
              )}
            </aside>

            <main
              key={gamePageKey}
              className="lg:col-span-3 border border-green-900/60 bg-green-950/5 p-6 md:p-8 relative min-h-[450px] lg:min-h-[634px] flex flex-col rounded-lg overflow-hidden order-1 lg:order-2 shadow-[0_0_40px_rgba(0,0,0,0.5)] animate-in zoom-in-95 duration-700"
            >
              {searchLoading && (
                <div className="absolute inset-0 z-40 bg-black/90 flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-300">
                  <div className="relative">
                    <RefreshCw className="w-16 h-16 text-green-500 animate-spin mb-4" />
                    <div className="absolute inset-0 blur-xl bg-green-500/20 animate-pulse" />
                  </div>
                  <span className="font-black uppercase tracking-[0.6em] text-sm animate-pulse">
                    Accessing Mainframe...
                  </span>
                </div>
              )}
              {loading && (
                <div className="absolute inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4 text-center animate-in fade-in duration-500 overflow-hidden">
                  <canvas
                    ref={scanCanvasRef}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                  />

                  {/* Simple scanline overlay */}
                  <div className="absolute inset-0 pointer-events-none z-10">
                    <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(34,197,94,0.03)_50%)] bg-[length:100%_4px] animate-[scanline-fast_0.5s_linear_infinite]" />
                  </div>

                  {/* RECTANGULAR BOX - smaller, less padding */}
                  <div className="relative z-20 flex flex-col items-center gap-3 bg-black/80 backdrop-blur-md px-8 py-5 rounded-md border border-green-500/60 shadow-[0_0_40px_rgba(34,197,94,0.3)] animate-pulse-glow min-w-[280px] max-w-[400px]">
                    {/* Simple corner brackets - smaller */}
                    <div className="absolute top-1 left-1 w-3 h-3 border-t border-l border-green-500/50" />
                    <div className="absolute top-1 right-1 w-3 h-3 border-t border-r border-green-500/50" />
                    <div className="absolute bottom-1 left-1 w-3 h-3 border-b border-l border-green-500/50" />
                    <div className="absolute bottom-1 right-1 w-3 h-3 border-b border-r border-green-500/50" />

                    {/* ICON ROW - smaller icons */}
                    <div className="flex items-center justify-center gap-2">
                      <Radar
                        size={16}
                        className="text-green-400 animate-pulse"
                      />
                      <Cpu
                        size={16}
                        className="text-green-400 animate-spin-slow"
                      />
                      <Wifi
                        size={16}
                        className="text-green-400 animate-pulse"
                      />
                      <Database
                        size={16}
                        className="text-green-400 animate-pulse"
                      />
                    </div>

                    {/* Status badge - compact */}
                    <div className="bg-green-950/60 border border-green-500/40 rounded-full px-3 py-0.5">
                      <span className="text-green-400 font-mono text-[8px] tracking-[0.2em] font-black flex items-center gap-1">
                        <Activity size={8} className="animate-pulse" />
                        SCAN v2.4.7
                      </span>
                    </div>

                    {/* Main text - smaller */}
                    <div className="relative">
                      <div className="text-green-400 font-mono text-xl md:text-2xl font-black tracking-wider drop-shadow-[0_0_15px_rgba(34,197,94,0.8)] relative z-10">
                        SCANNING_THE_WEB...
                      </div>
                    </div>

                    {/* Single line dots instead of 3 dots */}
                    <div className="flex gap-0.5">
                      <div className="w-1 h-1 bg-green-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <div className="w-1 h-1 bg-green-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <div className="w-1 h-1 bg-green-500 rounded-full animate-bounce" />
                    </div>

                    {/* Data packets - compact */}
                    <div className="flex items-center gap-2 bg-black/60 px-3 py-0.5 rounded border border-green-500/30">
                      <Database size={8} className="text-green-500" />
                      <span className="text-green-500 font-mono text-[8px] tracking-wide font-black">
                        {Math.floor(Math.random() * 9999)
                          .toString(16)
                          .toUpperCase()}
                        _PKT
                      </span>
                    </div>

                    {/* Progress bar - thinner */}
                    <div className="w-full max-w-[200px] h-[1px] bg-green-950/50 overflow-hidden">
                      <div className="w-1/2 h-full bg-green-500 animate-[loading-bar_1.5s_ease-in-out_infinite]" />
                    </div>
                  </div>
                </div>
              )}

              {!loading &&
                !suggestions &&
                !results &&
                !scanError &&
                !searchLoading && (
                  <div className="flex-1 flex flex-col items-center justify-center p-4 space-y-4 opacity-40 text-center animate-in fade-in duration-1000">
                    {results?.error === "not_found" ? (
                      <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6 animate-in zoom-in-90">
                        <div className="relative">
                          <ShieldAlert
                            size={100}
                            className="text-red-900 animate-pulse"
                          />
                          <div className="absolute inset-0 blur-2xl bg-red-900/20" />
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-red-500 font-black text-4xl uppercase italic tracking-tighter">
                            Game Not Found
                          </h3>
                          <p className="text-[10px] text-red-900 font-bold uppercase tracking-[0.4em]">
                            THE TITLE IS NOT IN DATABASE
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="relative group cursor-crosshair">
                          <div className="absolute inset-0 blur-3xl bg-green-500/10 group-hover:bg-green-500/20 transition-all duration-1000" />
                          <Globe
                            size={120}
                            className="text-green-800 animate-pulse-slow relative z-10"
                          />
                          <div className="absolute inset-0 flex items-center justify-center z-20">
                            <Search
                              size={40}
                              className="text-green-600 group-hover:scale-125 transition-transform duration-500"
                            />
                          </div>
                          <div className="absolute -inset-10 border border-green-500/5 rounded-full animate-spin-slow" />
                          <div className="absolute -inset-20 border border-green-500/5 rounded-full animate-reverse-spin-slow" />
                        </div>
                        <div className="flex flex-col items-center">
                          <h2 className="text-4xl font-black uppercase italic tracking-tighter text-green-700 w-full drop-shadow-[0_0_15px_rgba(34,197,94,0.4)]">
                            READY TO SEARCH
                          </h2>
                          <p className="text-[10px] font-bold uppercase tracking-[0.5em] mt-2 text-green-800 border-t border-green-600/40 pt-3 w-full text-center">
                            INPUT QUERY TO INITIATE SCAN_ARRAY
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}

              {suggestions && !searchLoading && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col h-full min-h-0">
                  <div className="flex justify-between items-end border-b border-green-900/50 pb-2">
                    <p className="text-xs text-yellow-500 font-black uppercase tracking-[0.2em]">
                      RESULTS:
                    </p>
                    <span className="text-[8px] text-green-900 uppercase">
                      Limit: 15 results
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto pr-2 custom-scrollbar flex-1 content-start pb-4">
                    {suggestions.map((s) => (
                      <button
                        key={s.game_info.id}
                        onClick={() =>
                          selectGame(s.game_info.id, s.title, s.image)
                        }
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setContextMenu({
                            visible: true,
                            x: e.clientX,
                            y: e.clientY,
                            url: `/?id=${s.game_info.id}&title=${encodeURIComponent(s.title)}`,
                            gameTitle: s.title,
                            gameId: s.game_info.id,
                            gameImage: s.image,
                          });
                        }}
                        onMouseEnter={() => sfx("hover")}
                        className="aspect-[3/4] w-full relative group overflow-hidden rounded border border-green-500/40 shadow-[0_0_10px_rgba(34,197,94,0.08)] hover:border-green-500 transition-all shadow-sm hover:shadow-[0_0_30px_rgba(34,197,94,0.3)]"
                      >
                        <img
                          src={s.image}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent opacity-80" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 z-30 bg-black/60 backdrop-blur-sm border-t border-green-500/30">
                          <span className="text-[10px] font-black uppercase text-green-400 block truncate">
                            {s.title}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {results && !results.error && !loading && (
                <div className="space-y-8 flex-1 flex flex-col animate-in slide-in-from-bottom-6 duration-700 min-h-0">
                  <div className="flex flex-col lg:flex-row gap-8 bg-black/60 p-6 rounded-lg border border-green-900/50 relative shadow-[0_0_40px_rgba(34,197,94,0.15)] overflow-hidden group items-stretch relative">
                    <div className="absolute inset-0 bg-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                    <div
                      ref={badgeContainerRef}
                      className="relative z-10 w-full lg:w-48 shrink-0 flex flex-col h-full"
                    >
                      <div className="relative">
                        <img
                          src={
                            results.thumb ||
                            "https://placehold.co/200x300/0a0f0a/22c55e?text=No+Image"
                          }
                          alt=""
                          className="w-full h-auto object-contain border-2 border-green-600/60 rounded-lg shadow-2xl relative z-20 grayscale-0 group-hover:brightness-110 transition-all duration-500"
                        />
                        <div className="absolute -inset-4 bg-green-500/10 blur-2xl z-10 animate-pulse" />
                      </div>
                      <div className="flex-grow"></div>
                      {/* 🔴 ALWAYS DOUBLE CHECK BADGE - Desktop only, absolutely positioned (no space) */}
                      <div
                        id="dynamic-badge"
                        className="hidden sm:block absolute left-0 right-0 z-30"
                        style={{ bottom: "0", transform: "translateY(0)" }}
                      >
                        <div className="border border-amber-500/40 rounded overflow-hidden shadow-[0_0_10px_rgba(245,158,11,0.2)] bg-black/90 backdrop-blur-sm">
                          <div className="bg-amber-600/30 text-amber-300 text-[12px] font-black uppercase tracking-wider text-center py-1.5">
                            <AlertTriangle
                              size={12}
                              className="inline mr-1 -mt-0.5"
                            />{" "}
                            We're Still in Beta
                          </div>
                          <div className="h-[1px] bg-amber-500/30" />
                          <div className="bg-amber-950/30 text-amber-400/80 text-[9px] font-bold uppercase tracking-wide text-center py-1.5">
                            Price updates can lag (sometimes)
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 space-y-6 w-full relative z-10 h-full">
                      <div className="space-y-1">
                        <span className="text-[10px] font-black text-green-700 uppercase tracking-[0.5em] block">
                          Target Analysis Complete
                        </span>
                        <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white uppercase italic drop-shadow-[0_0_20px_rgba(34,197,94,0.6)] animate-title-flicker">
                          {results.title}
                        </h2>
                        <div className="pt-3 pb-0">
                          <div className="w-full h-[2px] bg-gradient-to-r from-green-500 via-green-500/60 to-transparent rounded-full" />
                          <div className="w-full h-[1px] bg-gradient-to-r from-green-600/40 to-transparent mt-1" />
                        </div>
                      </div>
                      {(() => {
                        // Get all deals by category
                        const officialDeals = results.deals.filter(
                          (d) => d.storeType === "Official Store",
                        );
                        const keySellerDeals = results.deals.filter(
                          (d) => d.storeType === "Key Seller",
                        );
                        const marketplaceDeals = results.deals.filter(
                          (d) => d.storeType === "Marketplace",
                        );

                        // Calculate cheapest in each category (using your original retail values)
                        const cheapestOfficial =
                          officialDeals.length > 0
                            ? officialDeals.reduce(
                                (min, d) => (d.price < min.price ? d : min),
                                officialDeals[0],
                              )
                            : null;
                        const cheapestKeySeller =
                          keySellerDeals.length > 0
                            ? keySellerDeals.reduce(
                                (min, d) => (d.price < min.price ? d : min),
                                keySellerDeals[0],
                              )
                            : null;
                        const cheapestMarketplace =
                          marketplaceDeals.length > 0
                            ? marketplaceDeals.reduce(
                                (min, d) => (d.price < min.price ? d : min),
                                marketplaceDeals[0],
                              )
                            : null;

                        // ✅ USE THE EXISTING retail FROM EACH CHEAPEST DEAL (from your original API logic)
                        // All retail values already come from official stores only
                        const officialRetail = cheapestOfficial?.retail || null;
                        const keySellerRetail =
                          cheapestKeySeller?.retail || null;
                        const marketplaceRetail =
                          cheapestMarketplace?.retail || null;

                        // Discount percentages (based on the official retail from each deal's retail property)
                        const officialDiscountPercent =
                          cheapestOfficial &&
                          officialRetail &&
                          officialRetail > cheapestOfficial.price
                            ? Math.round(
                                ((officialRetail - cheapestOfficial.price) /
                                  officialRetail) *
                                  100,
                              )
                            : 0;
                        const keySellerDiscountPercent =
                          cheapestKeySeller &&
                          keySellerRetail &&
                          keySellerRetail > cheapestKeySeller.price
                            ? Math.round(
                                ((keySellerRetail - cheapestKeySeller.price) /
                                  keySellerRetail) *
                                  100,
                              )
                            : 0;
                        const marketplaceDiscountPercent =
                          cheapestMarketplace &&
                          marketplaceRetail &&
                          marketplaceRetail > cheapestMarketplace.price
                            ? Math.round(
                                ((marketplaceRetail -
                                  cheapestMarketplace.price) /
                                  marketplaceRetail) *
                                  100,
                              )
                            : 0;

                        // Find best deal for gold badge (ONLY Official + Key Seller)
                        const eligibleForBestDeal = [
                          ...(cheapestOfficial
                            ? [
                                {
                                  type: "official",
                                  price: cheapestOfficial.price,
                                  name: "OFFICIAL",
                                },
                              ]
                            : []),
                          ...(cheapestKeySeller
                            ? [
                                {
                                  type: "keySeller",
                                  price: cheapestKeySeller.price,
                                  name: "KEY SELLER",
                                },
                              ]
                            : []),
                        ];
                        const bestPrice =
                          eligibleForBestDeal.length > 0
                            ? Math.min(
                                ...eligibleForBestDeal.map((p) => p.price),
                              )
                            : null;
                        const bestDealType = eligibleForBestDeal.find(
                          (p) => p.price === bestPrice,
                        )?.type;

                        // Words for each column (only ONE line each)
                        const officialWord = "OFFICIAL STORE";
                        const keySellerWord = "DIRECT SELLER";
                        const marketplaceWord = "P2P MARKET";

                        return (
                          <div className="mb-4">
                            {/* 3-COLUMN GRID - TIGHT SPACING */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              {/* ========== OFFICIAL COLUMN ========== */}
                              <div
                                className={`relative bg-gradient-to-br from-blue-950/60 to-blue-900/30 border-2 ${bestDealType === "official" ? "border-amber-500 shadow-[0_0_25px_rgba(245,158,11,0.4)]" : "border-blue-500/70"} rounded-xl p-4 text-center group hover:border-blue-400 hover:shadow-[0_0_25px_rgba(59,130,246,0.35)] transition-all duration-300 overflow-visible`}
                              >
                                <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/5 transition-all duration-500 rounded-xl" />
                                <div className="absolute -inset-1 bg-blue-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl pointer-events-none" />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full bg-blue-500/5 blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />

                                {/* BEST DEAL BADGE - TOP RIGHT, BIGGER */}
                                {bestDealType === "official" && (
                                  <div className="absolute -top-3 right-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-black text-[10px] px-2.5 py-0.5 rounded-full flex items-center gap-1.5 shadow-[0_0_14px_rgba(245,158,11,0.9)] z-10">
                                    <Trophy size={10} fill="black" /> BEST DEAL
                                  </div>
                                )}

                                <div className="flex items-center justify-center gap-2 mb-2">
                                  <ShieldCheck
                                    size={18}
                                    className="text-blue-400 drop-shadow-[0_0_4px_rgba(59,130,246,0.6)]"
                                  />
                                  <span className="text-blue-400 font-black text-[11px] uppercase tracking-wider">
                                    OFFICIAL Seller
                                  </span>
                                </div>

                                {cheapestOfficial ? (
                                  <>
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                      <Zap
                                        size={16}
                                        className="text-blue-400 fill-blue-400 animate-pulse"
                                      />
                                      <span className="text-3xl font-black text-blue-400 drop-shadow-[0_0_8px_rgba(59,130,246,0.4)]">
                                        ${cheapestOfficial.price}
                                      </span>
                                    </div>

                                    {/* BOLD DISCOUNT PILL - RECTANGULAR + SHARP */}
                                    {officialDiscountPercent > 0 ? (
                                      <div className="inline-flex items-center gap-1.5 bg-amber-500/15 border-2 border-amber-500 px-3 py-1 rounded-md mb-2 shadow-[0_0_10px_rgba(245,158,11,0.5)]">
                                        <Zap
                                          size={12}
                                          className="text-amber-400 fill-amber-400"
                                        />
                                        <span className="text-[11px] font-black text-amber-400 tracking-wide">
                                          {officialDiscountPercent}% DISCOUNT
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="inline-flex items-center gap-1.5 bg-white/5 border-2 border-white/10 px-3 py-1 rounded-md mb-2">
                                        <Zap
                                          size={12}
                                          className="text-gray-500 fill-gray-500"
                                        />
                                        <span className="text-[11px] font-black text-gray-500 tracking-wide">
                                          % DISCOUNT
                                        </span>
                                      </div>
                                    )}

                                    <div className="text-[9px] text-blue-500/60 mb-2">
                                      {officialDeals.length} listing
                                      {officialDeals.length !== 1 ? "s" : ""}
                                    </div>

                                    <div className="flex items-center justify-center gap-1 text-[8px] text-yellow-500/70">
                                      <div className="w-1 h-1 bg-yellow-500 rounded-full" />
                                      <span>{officialWord}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-[10px] text-blue-500/50 py-4">
                                    No official stores
                                  </div>
                                )}

                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-blue-500/30 rounded-br-lg opacity-50" />
                              </div>

                              {/* ========== KEY SELLER COLUMN ========== */}
                              <div
                                className={`relative bg-gradient-to-br from-purple-950/60 to-purple-900/30 border-2 ${bestDealType === "keySeller" ? "border-amber-500 shadow-[0_0_25px_rgba(245,158,11,0.4)]" : "border-purple-500/70"} rounded-xl p-4 text-center group hover:border-purple-400 hover:shadow-[0_0_25px_rgba(147,51,234,0.35)] transition-all duration-300 overflow-visible`}
                              >
                                <div className="absolute inset-0 bg-purple-500/0 group-hover:bg-purple-500/5 transition-all duration-500 rounded-xl" />
                                <div className="absolute -inset-1 bg-purple-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl pointer-events-none" />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full bg-purple-500/5 blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />

                                {bestDealType === "keySeller" && (
                                  <div className="absolute -top-3 right-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-black text-[10px] px-2.5 py-0.5 rounded-full flex items-center gap-1.5 shadow-[0_0_14px_rgba(245,158,11,0.9)] z-10">
                                    <Trophy size={10} fill="black" /> BEST DEAL
                                  </div>
                                )}

                                <div className="flex items-center justify-center gap-2 mb-2">
                                  <Tag
                                    size={18}
                                    className="text-purple-400 drop-shadow-[0_0_4px_rgba(147,51,234,0.6)]"
                                  />
                                  <span className="text-purple-400 font-black text-[11px] uppercase tracking-wider">
                                    Third-Party SELLER
                                  </span>
                                </div>

                                {cheapestKeySeller ? (
                                  <>
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                      <Zap
                                        size={16}
                                        className="text-purple-400 fill-purple-400 animate-pulse"
                                      />
                                      <span className="text-3xl font-black text-purple-400 drop-shadow-[0_0_8px_rgba(147,51,234,0.4)]">
                                        ${cheapestKeySeller.price}
                                      </span>
                                    </div>

                                    {/* BOLD DISCOUNT PILL - RECTANGULAR + SHARP */}
                                    {keySellerDiscountPercent > 0 ? (
                                      <div className="inline-flex items-center gap-1.5 bg-amber-500/15 border-2 border-amber-500 px-3 py-1 rounded-md mb-2 shadow-[0_0_10px_rgba(245,158,11,0.5)]">
                                        <Zap
                                          size={12}
                                          className="text-amber-400 fill-amber-400"
                                        />
                                        <span className="text-[11px] font-black text-amber-400 tracking-wide">
                                          {keySellerDiscountPercent}% DISCOUNT
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="inline-flex items-center gap-1.5 bg-white/5 border-2 border-white/10 px-3 py-1 rounded-md mb-2">
                                        <Zap
                                          size={12}
                                          className="text-gray-500 fill-gray-500"
                                        />
                                        <span className="text-[11px] font-black text-gray-500 tracking-wide">
                                          % DISCOUNT
                                        </span>
                                      </div>
                                    )}

                                    <div className="text-[9px] text-purple-500/60 mb-2">
                                      {keySellerDeals.length} listing
                                      {keySellerDeals.length !== 1 ? "s" : ""}
                                    </div>

                                    <div className="flex items-center justify-center gap-1 text-[8px] text-yellow-500/70">
                                      <div className="w-1 h-1 bg-yellow-500 rounded-full" />
                                      <span>{keySellerWord}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-[10px] text-purple-500/50 py-4">
                                    No third-party sellers
                                  </div>
                                )}

                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-purple-500/30 rounded-br-lg opacity-50" />
                              </div>

                              {/* ========== MARKETPLACE COLUMN ========== */}
                              <div
                                className={`relative bg-gradient-to-br from-red-950/60 to-red-900/30 border-2 ${bestDealType === "marketplace" ? "border-amber-500 shadow-[0_0_25px_rgba(245,158,11,0.4)]" : "border-red-500/70"} rounded-xl p-4 text-center group hover:border-red-400 hover:shadow-[0_0_25px_rgba(220,38,38,0.35)] transition-all duration-300 overflow-visible`}
                              >
                                <div className="absolute inset-0 bg-red-500/0 group-hover:bg-red-500/5 transition-all duration-500 rounded-xl" />
                                <div className="absolute -inset-1 bg-red-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl pointer-events-none" />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full bg-red-500/5 blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />

                                {/* No BEST DEAL badge for Marketplace */}

                                <div className="flex items-center justify-center gap-2 mb-2">
                                  <AlertTriangle
                                    size={18}
                                    className="text-red-400 drop-shadow-[0_0_4px_rgba(220,38,38,0.6)]"
                                  />
                                  <span className="text-red-400 font-black text-[11px] uppercase tracking-wider">
                                    MARKETPLACE
                                  </span>
                                </div>

                                {cheapestMarketplace ? (
                                  <>
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                      <Zap
                                        size={16}
                                        className="text-red-400 fill-red-400 animate-pulse"
                                      />
                                      <span className="text-3xl font-black text-red-400 drop-shadow-[0_0_8px_rgba(220,38,38,0.4)]">
                                        ${cheapestMarketplace.price}
                                      </span>
                                    </div>

                                    {/* BOLD DISCOUNT PILL - RECTANGULAR + SHARP */}
                                    {marketplaceDiscountPercent > 0 ? (
                                      <div className="inline-flex items-center gap-1.5 bg-amber-500/10 border-2 border-amber-500 px-3 py-1 rounded-md mb-2 shadow-[0_0_10px_rgba(245,158,11,0.5)]">
                                        <Zap
                                          size={12}
                                          className="text-amber-400 fill-amber-400"
                                        />
                                        <span className="text-[11px] font-black text-amber-400 tracking-wide">
                                          {marketplaceDiscountPercent}% DISCOUNT
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="inline-flex items-center gap-1.5 bg-white/5 border-2 border-white/10 px-3 py-1 rounded-md mb-2">
                                        <Zap
                                          size={12}
                                          className="text-gray-500 fill-gray-500"
                                        />
                                        <span className="text-[11px] font-black text-gray-500 tracking-wide">
                                          % DISCOUNT
                                        </span>
                                      </div>
                                    )}

                                    <div className="text-[9px] text-red-500/60 mb-2">
                                      {marketplaceDeals.length} listing
                                      {marketplaceDeals.length !== 1 ? "s" : ""}
                                    </div>

                                    <div className="flex items-center justify-center gap-1 text-[8px] text-yellow-500/70">
                                      <div className="w-1 h-1 bg-yellow-500 rounded-full animate-pulse" />
                                      <span>{marketplaceWord}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-[10px] text-red-500/50 py-4">
                                    No marketplaces
                                  </div>
                                )}

                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-red-500/30 rounded-br-lg opacity-50" />
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* 🔴 ALWAYS DOUBLE CHECK BADGE - Mobile only */}
                      <div className="block sm:hidden mt-4 border border-amber-500/40 rounded overflow-hidden shadow-[0_0_10px_rgba(245,158,11,0.2)]">
                        <div className="bg-amber-600/30 text-amber-300 text-[12px] font-black uppercase tracking-wider text-center py-1.5 flex items-center justify-center gap-1">
                          <AlertTriangle size={12} /> We're Still in Beta
                        </div>
                        <div className="h-[1px] bg-amber-500/30" />
                        <div className="bg-amber-950/30 text-amber-400/80 text-[9px] font-bold uppercase tracking-wide text-center py-1.5">
                          Price updates can lag (sometimes)
                        </div>
                      </div>{" "}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Store Count Header with Buttons */}
                    <div className="text-[12px] sm:text-[16px] font-black text-green-400 uppercase tracking-wider mb-3 sm:mb-5 border-b-2 border-green-600/70 pb-2 sm:pb-3 flex items-center justify-between flex-wrap gap-2 sm:gap-3">
                      <div className="flex items-center gap-3">
                        <Layers size={22} className="text-green-500" />
                        <span>AVAILABLE DEALS</span>
                        <span className="text-[10px] text-green-400 bg-green-950/60 px-2 py-0.5 rounded-md border border-green-500/50 font-mono">
                          {results.storeCounts?.total || results.deals.length}{" "}
                          STORES
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            sfx("click");
                            setIsAlertModalOpen(true);
                          }}
                          onMouseEnter={() => sfx("hover")}
                          className="flex items-center gap-1.5 text-[10px] font-black uppercase text-green-400 border border-green-500/40 px-3 py-1.5 hover:bg-green-500 hover:text-black hover:border-green-500 transition-all rounded-md bg-black/40"
                        >
                          <Bell size={12} className="animate-swing" /> PRICE
                          ALERT
                        </button>
                        <button
                          onClick={() => {
                            sfx("click");
                            setShareGameTitle(results.title);
                            setShareGameId(results.gameId || "");
                            setShowShareModal(true);
                          }}
                          onMouseEnter={() => sfx("hover")}
                          className="flex items-center gap-1.5 text-[10px] font-black uppercase text-green-400 border border-green-500/40 px-3 py-1.5 hover:bg-green-500 hover:text-black hover:border-green-500 transition-all rounded-md bg-black/40"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                            />
                          </svg>
                          SHARE
                        </button>
                      </div>
                    </div>

                    {/* Categorized Deals */}
                    <div className="space-y-5 overflow-y-auto pr-2 custom-scrollbar flex-1 pb-6">
                      {/* ========== OFFICIAL STORES SECTION ========== */}
                      {(() => {
                        const officialDeals = results.deals.filter(
                          (d) => d.storeType === "Official Store",
                        );
                        const keySellerDealsForCheck = results.deals.filter(
                          (d) => d.storeType === "Key Seller",
                        );
                        const cheapestOfficialForCheck =
                          officialDeals.length > 0
                            ? officialDeals.reduce(
                                (min, d) => (d.price < min.price ? d : min),
                                officialDeals[0],
                              )
                            : null;
                        const cheapestKeySellerForCheck =
                          keySellerDealsForCheck.length > 0
                            ? keySellerDealsForCheck.reduce(
                                (min, d) => (d.price < min.price ? d : min),
                                keySellerDealsForCheck[0],
                              )
                            : null;
                        const cheapestOfficialPrice =
                          cheapestOfficialForCheck?.price ?? Infinity;
                        const cheapestKeySellerPrice =
                          cheapestKeySellerForCheck?.price ?? Infinity;
                        const officialWinsOrTies =
                          cheapestOfficialPrice <= cheapestKeySellerPrice;
                        if (officialDeals.length === 0) return null;
                        const bestDealIndex = officialDeals.reduce(
                          (minIdx, d, idx, arr) =>
                            d.price < arr[minIdx].price ? idx : minIdx,
                          0,
                        );
                        const bestDeal = officialDeals[bestDealIndex];
                        const hasAnyDiscount =
                          bestDeal && bestDeal.retail > bestDeal.price;
                        const hasSignificantDiscount =
                          bestDeal && bestDeal.retail - bestDeal.price > 5;

                        return (
                          <div className="border border-blue-500/40 rounded-md overflow-hidden shadow-[0_0_20px_rgba(59,130,246,0.15)] bg-black/80">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-blue-900/40 to-blue-950/50 px-3 sm:px-5 py-2 sm:py-3 border-b border-blue-500/40">
                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3">
                                  <ShieldCheck
                                    size={20}
                                    className="text-blue-400"
                                  />
                                  <span className="text-[12px] sm:text-[18px] font-black uppercase text-blue-400 tracking-wider">
                                    OFFICIAL & AUTHORIZED
                                  </span>
                                  <span className="text-[11px] text-blue-400 bg-blue-950/60 px-2 py-0.5 rounded-md border border-blue-500/40 font-mono">
                                    {officialDeals.length} stores
                                  </span>
                                </div>
                                <div className="bg-green-600 px-3 py-1.5 rounded-md shadow-[0_0_10px_rgba(34,197,94,0.5)]">
                                  <span className="text-[11px] font-black text-black uppercase tracking-wider flex items-center gap-2 [-webkit-text-stroke:_0.5px_black] [text-stroke:_0.5px_black]">
                                    <span className="w-1.5 h-1.5 bg-black rounded-full shadow-sm"></span>
                                    FULLY LICENSED ✓
                                  </span>
                                </div>
                              </div>
                            </div>
                            {/* Deals List */}
                            <div className="divide-y divide-green-800/60">
                              {officialDeals
                                .slice(
                                  0,
                                  showAllOfficial ? officialDeals.length : 3,
                                )
                                .map((d, idx) => {
                                  const isGolden =
                                    idx === bestDealIndex && hasAnyDiscount;
                                  const isBest = isGolden && officialWinsOrTies;
                                  return (
                                    <button
                                      key={idx}
                                      onClick={() => {
                                        sfx("click");
                                        const coupon = getManualCoupon(d.store);
                                        if (coupon) {
                                          setActiveRedirect({
                                            ...d,
                                            manualCouponType: coupon.type,
                                            manualCouponCode: coupon.code,
                                          });
                                        } else {
                                          setActiveRedirect(d);
                                        }
                                      }}
                                      onMouseEnter={() => sfx("hover")}
                                      className={`w-full flex justify-between items-center p-4 transition-all duration-300 group/item ${
                                        isGolden
                                          ? "bg-gradient-to-r from-yellow-950/30 to-green-950/40 border-l-4 border-yellow-500"
                                          : "bg-black/40 hover:bg-green-950/30"
                                      }`}
                                    >
                                      <div className="flex items-center gap-4 flex-wrap flex-1">
                                        {d.storeImage && (
                                          <img
                                            src={d.storeImage}
                                            alt={d.store}
                                            className="w-10 h-10 object-contain transition-all duration-300 group-hover/item:scale-110 group-hover/item:drop-shadow-[0_0_10px_#22c55e]"
                                            onError={(e) =>
                                              (e.target.style.display = "none")
                                            }
                                          />
                                        )}
                                        <div className="flex flex-col items-start">
                                          <span className="font-black uppercase text-xs sm:text-base md:text-lg text-white tracking-wide">
                                            {d.edition !== "Standard"
                                              ? d.edition
                                              : d.store}
                                          </span>
                                          {d.edition !== "Standard" && (
                                            <span className="text-[10px] text-green-500 font-mono mt-0.5">
                                              {d.store}
                                            </span>
                                          )}
                                        </div>
                                        {d.isMac && (
                                          <span className="text-[10px] font-bold text-purple-300 border border-purple-500/50 px-2 py-0.5">
                                            🍎 MAC ONLY
                                          </span>
                                        )}
                                        {d.platform &&
                                          !d.isMac &&
                                          d.platform !== "WINDOWS" && (
                                            <span className="text-[10px] font-bold text-cyan-400 border border-cyan-500/50 px-2 py-0.5">
                                              💻 {d.platform}
                                            </span>
                                          )}
                                        {isBest && (
                                          <span className="bg-yellow-500 text-black px-3 py-1 rounded-xl font-black text-[11px] uppercase flex items-center gap-1.5 shadow-[0_0_12px_rgba(234,179,8,0.6)] animate-pulse ml-2">
                                            BEST DEAL 🏆
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                          {(() => {
                                            // Check API coupon (Type 1)
                                            const apiCoupon =
                                              d.coupon?.available &&
                                              d.coupon?.code;
                                            // Check manual coupon (Type 2)
                                            const manualCoupon =
                                              getManualCoupon(d.store);

                                            let couponText = null;
                                            let couponColor = "";

                                            if (apiCoupon) {
                                              // Try to extract discount % from coupon description
                                              const discountMatch =
                                                d.coupon?.description?.match(
                                                  /(\d+)%/,
                                                );
                                              const discountPercent =
                                                discountMatch
                                                  ? discountMatch[1]
                                                  : "";
                                              if (discountPercent) {
                                                couponText = `GET ${discountPercent}% OFF: ${d.coupon.code}`;
                                              } else {
                                                couponText = `USE CODE: ${d.coupon.code}`;
                                              }
                                              couponColor = "bg-green-400";
                                            } else if (
                                              manualCoupon &&
                                              manualCoupon.type === 2
                                            ) {
                                              couponText = `GET ${manualCoupon.discountPercent}% OFF: ${manualCoupon.code}`;
                                              couponColor = "bg-green-500";
                                            }

                                            if (couponText) {
                                              return (
                                                <span
                                                  className={`text-[13px] font-black text-black bg-green-500 px-1.5 py-0.5 rounded-md shadow-[0_0_12px_rgba(34,197,94,0.6)] tracking-tight leading-none`}
                                                >
                                                  {couponText}
                                                </span>
                                              );
                                            }
                                            return null;
                                          })()}
                                          <span
                                            className={`font-black text-xl ${isGolden ? "text-yellow-400" : "text-green-400"}`}
                                          >
                                            ${d.price.toFixed(2)}
                                          </span>
                                        </div>
                                        <ChevronRight
                                          size={20}
                                          className="text-green-600 group-hover/item:text-green-400 group-hover/item:translate-x-1 transition-all"
                                        />
                                      </div>
                                    </button>
                                  );
                                })}
                            </div>
                            {officialDeals.length > 3 && (
                              <div className="p-3 text-center border-t border-blue-500/40 bg-gradient-to-r from-blue-950/30 to-black">
                                <button
                                  onClick={() =>
                                    setShowAllOfficial(!showAllOfficial)
                                  }
                                  onMouseEnter={() => sfx("hover")}
                                  className="group relative overflow-hidden px-6 py-2.5 bg-blue-600/20 border-2 border-blue-500 rounded-md hover:bg-blue-600 hover:border-blue-400 transition-all duration-300 shadow-[0_0_15px_rgba(59,130,246,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.6)] w-full"
                                >
                                  <span className="relative z-10 text-blue-400 group-hover:text-black font-black text-[11px] sm:text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                                    {showAllOfficial ? (
                                      <>
                                        <span>▲ COLLAPSE</span>
                                        <span className="text-[10px]">
                                          (-{officialDeals.length - 3})
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span>▼ EXPAND ALL</span>
                                        <span className="bg-yellow-500 text-black rounded-full px-1.5 py-0.5 text-[9px] font-black ml-1">
                                          +{officialDeals.length - 3} MORE
                                        </span>
                                        <ChevronRight
                                          size={14}
                                          className="group-hover:translate-x-1 transition-transform"
                                        />
                                      </>
                                    )}
                                  </span>
                                  <div className="absolute inset-0 bg-blue-500 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ========== KEY SELLERS SECTION ========== */}
                      {(() => {
                        const keySellerDeals = results.deals.filter(
                          (d) => d.storeType === "Key Seller",
                        );
                        const officialDealsForCheck = results.deals.filter(
                          (d) => d.storeType === "Official Store",
                        );
                        const cheapestOfficialForKS =
                          officialDealsForCheck.length > 0
                            ? officialDealsForCheck.reduce(
                                (min, d) => (d.price < min.price ? d : min),
                                officialDealsForCheck[0],
                              )
                            : null;
                        const cheapestKeySellerForKS =
                          keySellerDeals.length > 0
                            ? keySellerDeals.reduce(
                                (min, d) => (d.price < min.price ? d : min),
                                keySellerDeals[0],
                              )
                            : null;
                        const officialWinsOrTies =
                          (cheapestOfficialForKS?.price ?? Infinity) <=
                          (cheapestKeySellerForKS?.price ?? Infinity);
                        if (keySellerDeals.length === 0) return null;
                        return (
                          <div className="border border-purple-600/50 rounded-md overflow-hidden shadow-[0_0_20px_rgba(147,51,234,0.1)] bg-black/80">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-purple-900/60 to-purple-950/70 px-5 py-3 border-b border-purple-500/40">
                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3">
                                  <div className="relative inline-flex group">
                                    <Info
                                      size={20}
                                      className="text-purple-400 cursor-help"
                                    />
                                    <div className="absolute top-full left-0 mt-2 px-4 py-2.5 bg-black border-2 border-purple-500 rounded-md shadow-[0_0_30px_rgba(147,51,234,0.5)] opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-[9999] w-80">
                                      <p className="text-[11px] text-purple-300 font-mono leading-relaxed">
                                        Unofficial sellers are third-party
                                        stores.
                                      </p>
                                      <p className="text-[10px] text-purple-400 font-mono mt-1 leading-relaxed">
                                        Good Reputation stores have proven
                                        delivery and refund policies.
                                      </p>
                                    </div>
                                  </div>
                                  <span className="text-[18px] font-black uppercase text-purple-400 tracking-wider">
                                    UNOFFICIAL SELLERS
                                  </span>
                                  <span className="text-[11px] text-purple-400 bg-purple-950/80 px-2 py-0.5 rounded-md border border-purple-500/50 font-mono">
                                    {keySellerDeals.length} stores
                                  </span>
                                </div>
                                <div className="border border-purple-500/50 px-3 py-1.5 rounded-md">
                                  <span className="text-[11px] font-black text-purple-400 uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-purple-400 animate-pulse rounded-full"></span>
                                    VERIFY REPUTATION
                                  </span>
                                </div>
                              </div>
                            </div>
                            {/* Deals List */}
                            <div className="divide-y divide-purple-800/50">
                              {keySellerDeals
                                .slice(
                                  0,
                                  showAllKeySellers ? keySellerDeals.length : 3,
                                )
                                .map((d, idx) => {
                                  console.log("Store name:", d.store);
                                  let reputationColor = "text-yellow-500";
                                  let reputationBg = "bg-yellow-950/40";
                                  let reputationBorder = "border-yellow-600/50";
                                  let reputationText = "Mixed Reputation";
                                  let emoji = "";
                                  let iconName = null;

                                  if (d.store === "Loaded") {
                                    reputationColor = "text-lime-400";
                                    reputationBg = "bg-lime-950/40";
                                    reputationBorder = "border-lime-600/50";
                                    reputationText = "Good Reputation";
                                    emoji = "";
                                  }
                                  if (d.store === "YUPLAY") {
                                    reputationColor = "text-lime-400";
                                    reputationBg = "bg-lime-950/40";
                                    reputationBorder = "border-lime-600/50";
                                    reputationText = "Good Reputation";
                                    emoji = "";
                                  }
                                  if (d.store === "Instant Gaming") {
                                    reputationColor = "text-lime-400";
                                    reputationBg = "bg-lime-950/40";
                                    reputationBorder = "border-lime-600/50";
                                    reputationText = "Good Reputation";
                                    emoji = "";
                                  }
                                  return (
                                    <button
                                      key={idx}
                                      onClick={() => {
                                        sfx("click");
                                        const coupon = getManualCoupon(d.store);
                                        if (coupon) {
                                          setActiveRedirect({
                                            ...d,
                                            manualCouponType: coupon.type,
                                            manualCouponCode: coupon.code,
                                          });
                                        } else {
                                          setActiveRedirect(d);
                                        }
                                      }}
                                      onMouseEnter={() => sfx("hover")}
                                      className={`w-full flex justify-between items-center p-4 transition-all duration-300 group/item ${
                                        !officialWinsOrTies && idx === 0
                                          ? "bg-gradient-to-r from-yellow-950/30 to-purple-950/40 border-l-4 border-yellow-500"
                                          : "bg-black/40 hover:bg-purple-950/20"
                                      }`}
                                    >
                                      <div className="flex items-center gap-4 flex-wrap flex-1">
                                        {d.storeImage && (
                                          <img
                                            src={d.storeImage}
                                            alt={d.store}
                                            className="w-10 h-10 object-contain transition-all duration-300 group-hover/item:scale-110 group-hover/item:drop-shadow-[0_0_10px_#eab308]"
                                            onError={(e) =>
                                              (e.target.style.display = "none")
                                            }
                                          />
                                        )}
                                        <div className="flex flex-col items-start">
                                          <span className="font-black uppercase text-xs sm:text-base md:text-lg text-white tracking-wide">
                                            {d.edition !== "Standard"
                                              ? d.edition
                                              : d.store}
                                          </span>
                                          {d.edition !== "Standard" && (
                                            <span className="text-[10px] text-purple-400 font-mono mt-0.5">
                                              {d.store}
                                            </span>
                                          )}
                                        </div>
                                        <span
                                          className={`text-[9px] font-bold ${reputationColor} ${reputationBg} border ${reputationBorder} px-2 py-0.5 flex items-center gap-1`}
                                        >
                                          {d.store === "Loaded" && (
                                            <CheckCircle2
                                              size={10}
                                              className="text-lime-400"
                                            />
                                          )}
                                          {d.store === "YUPLAY" && (
                                            <CheckCircle2
                                              size={10}
                                              className="text-lime-400"
                                            />
                                          )}
                                          {d.store === "Instant Gaming" && (
                                            <CheckCircle2
                                              size={10}
                                              className="text-lime-400"
                                            />
                                          )}
                                          {d.store !== "Loaded" &&
                                            d.store !== "YUPLAY" &&
                                            d.store !== "Instant Gaming" && (
                                              <AlertTriangle
                                                size={10}
                                                className="text-yellow-500"
                                              />
                                            )}
                                          {reputationText}
                                        </span>
                                        {d.isMac && (
                                          <span className="text-[10px] font-bold text-purple-300 border border-purple-500/50 px-2 py-0.5">
                                            🍎 MAC ONLY
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={`font-black text-xl ${!officialWinsOrTies && idx === 0 ? "text-yellow-400" : "text-purple-400"}`}
                                          >
                                            ${d.price.toFixed(2)}
                                          </span>
                                        </div>
                                        <ChevronRight
                                          size={20}
                                          className="text-purple-600 group-hover/item:text-purple-400 group-hover/item:translate-x-1 transition-all"
                                        />
                                      </div>
                                    </button>
                                  );
                                })}
                            </div>
                            {keySellerDeals.length > 3 && (
                              <div className="p-3 text-center border-t border-purple-500/40 bg-gradient-to-r from-purple-950/30 to-black">
                                <button
                                  onClick={() =>
                                    setShowAllKeySellers(!showAllKeySellers)
                                  }
                                  onMouseEnter={() => sfx("hover")}
                                  className="group relative overflow-hidden px-6 py-2.5 bg-purple-600/20 border-2 border-purple-500 rounded-md hover:bg-purple-600 hover:border-purple-400 transition-all duration-300 shadow-[0_0_15px_rgba(147,51,234,0.3)] hover:shadow-[0_0_25px_rgba(147,51,234,0.6)] w-full"
                                >
                                  <span className="relative z-10 text-purple-400 group-hover:text-black font-black text-[11px] sm:text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                                    {showAllKeySellers ? (
                                      <>
                                        <span>▲ COLLAPSE</span>
                                        <span className="text-[10px]">
                                          (-{keySellerDeals.length - 3})
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span>▼ EXPAND ALL</span>
                                        <span className="bg-yellow-500 text-black rounded-full px-1.5 py-0.5 text-[9px] font-black ml-1">
                                          +{keySellerDeals.length - 3} MORE
                                        </span>
                                        <ChevronRight
                                          size={14}
                                          className="group-hover:translate-x-1 transition-transform"
                                        />
                                      </>
                                    )}
                                  </span>
                                  <div className="absolute inset-0 bg-purple-500 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ========== MARKETPLACES SECTION ========== */}
                      {(() => {
                        const marketplaceDeals = results.deals.filter(
                          (d) => d.storeType === "Marketplace",
                        );
                        if (marketplaceDeals.length === 0) return null;
                        return (
                          <div className="border border-red-600/50 rounded-md overflow-hidden shadow-[0_0_20px_rgba(220,38,38,0.1)] bg-black/80">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-red-900/50 to-red-950/60 px-5 py-3 border-b border-red-500/40">
                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3">
                                  <AlertTriangle
                                    size={20}
                                    className="text-red-400"
                                  />
                                  <span className="text-[18px] font-black uppercase text-red-400 tracking-wider">
                                    KEY MARKETPLACES
                                  </span>
                                  <span className="text-[11px] text-red-500 bg-red-950/80 px-2 py-0.5 rounded-md border border-red-500/50 font-mono">
                                    {marketplaceDeals.length} stores
                                  </span>
                                </div>
                                <div className="border border-red-500/50 px-3 py-1.5 rounded-md">
                                  <span className="text-[11px] font-black text-red-400 uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-red-400 animate-pulse rounded-full"></span>
                                    PROCEED WITH CAUTION
                                  </span>
                                </div>
                              </div>
                            </div>
                            {/* Deals List */}
                            <div className="divide-y divide-red-800/50">
                              {marketplaceDeals
                                .slice(
                                  0,
                                  showAllMarketplaces
                                    ? marketplaceDeals.length
                                    : 3,
                                )
                                .map((d, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      sfx("click");
                                      setActiveRedirect(d);
                                    }}
                                    onMouseEnter={() => sfx("hover")}
                                    className="w-full flex justify-between items-center p-4 hover:bg-red-950/20 transition-all duration-300 group/item bg-black/40"
                                  >
                                    <div className="flex items-center gap-4 flex-wrap flex-1">
                                      {d.storeImage && (
                                        <img
                                          src={d.storeImage}
                                          alt={d.store}
                                          className="w-6 sm:w-10 h-6 sm:h-10 object-contain transition-all duration-300 group-hover/item:scale-110 group-hover/item:drop-shadow-[0_0_10px_#22c55e]"
                                          onError={(e) =>
                                            (e.target.style.display = "none")
                                          }
                                        />
                                      )}
                                      <div className="flex flex-col items-start">
                                        <span className="font-black uppercase text-xs sm:text-base md:text-lg text-white tracking-wide">
                                          {d.edition !== "Standard"
                                            ? d.edition
                                            : d.store}
                                        </span>
                                        {d.edition !== "Standard" && (
                                          <span className="text-[10px] text-red-500 font-mono mt-0.5">
                                            {d.store}
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-[9px] font-bold text-red-400 bg-red-950/40 border border-red-600/50 px-2 py-0.5 flex items-center gap-1">
                                        ⚠️ Use Caution
                                      </span>
                                      {d.isMac && (
                                        <span className="text-[10px] font-bold text-purple-300 border border-purple-500/50 px-2 py-0.5">
                                          🍎 MAC ONLY
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center gap-2">
                                        <span className="font-black text-xl text-red-400">
                                          ${d.price.toFixed(2)}
                                        </span>
                                      </div>
                                      <ChevronRight
                                        size={20}
                                        className="text-red-600 group-hover/item:text-red-400 group-hover/item:translate-x-1 transition-all"
                                      />
                                    </div>
                                  </button>
                                ))}
                            </div>
                            {marketplaceDeals.length > 3 && (
                              <div className="p-3 text-center border-t border-red-500/40 bg-gradient-to-r from-red-950/30 to-black">
                                <button
                                  onClick={() =>
                                    setShowAllMarketplaces(!showAllMarketplaces)
                                  }
                                  onMouseEnter={() => sfx("hover")}
                                  className="group relative overflow-hidden px-6 py-2.5 bg-red-600/20 border-2 border-red-500 rounded-md hover:bg-red-600 hover:border-red-400 transition-all duration-300 shadow-[0_0_15px_rgba(220,38,38,0.3)] hover:shadow-[0_0_25px_rgba(220,38,38,0.6)] w-full"
                                >
                                  <span className="relative z-10 text-red-400 group-hover:text-black font-black text-[11px] sm:text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                                    {showAllMarketplaces ? (
                                      <>
                                        <span>▲ COLLAPSE</span>
                                        <span className="text-[10px]">
                                          (-{marketplaceDeals.length - 3})
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span>▼ EXPAND ALL</span>
                                        <span className="bg-yellow-500 text-black rounded-full px-1.5 py-0.5 text-[9px] font-black ml-1">
                                          +{marketplaceDeals.length - 3} MORE
                                        </span>
                                        <ChevronRight
                                          size={14}
                                          className="group-hover:translate-x-1 transition-transform"
                                        />
                                      </>
                                    )}
                                  </span>
                                  <div className="absolute inset-0 bg-red-500 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
              {results?.error === "not_found" && !loading && (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6 space-y-8 animate-in zoom-in-90">
                  <div className="relative">
                    <ShieldAlert
                      size={100}
                      className="text-red-900 animate-pulse"
                    />
                    <div className="absolute inset-0 blur-2xl bg-red-900/20" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-red-500 font-black text-4xl uppercase italic tracking-tighter">
                      Game Not Found
                    </h3>
                    <p className="text-[10px] text-red-900 font-bold uppercase tracking-[0.4em]">
                      THE TITLE IS NOT IN DATABASE
                    </p>
                  </div>
                  {/* BETA NOTICE - Now under the modal */}
                  <div className="w-full max-w-md bg-yellow-950/40 border-l-8 border-yellow-500 p-4 rounded-r-xl shadow-[0_0_20px_rgba(234,179,8,0.2)] text-left mt-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        size={24}
                        className="text-yellow-500 flex-shrink-0 mt-0.5"
                      />
                      <div>
                        <p className="text-yellow-500 font-black text-xs uppercase tracking-wide mb-1"></p>
                        <p className="text-yellow-500 text-xs leading-relaxed">
                          WE'RE STILL IN BETA — SOME LESSER-KNOWN GAMES MAY NOT
                          BE INCLUDED IN THE DB YET. BUT WE'RE WORKING ON
                          INCLUDING ALL GAMES IN THE FUTURE.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setResults(null)}
                    onMouseEnter={() => sfx("hover")}
                    className="text-[10px] font-bold text-green-800 uppercase underline tracking-widest hover:text-green-400 transition-colors"
                  >
                    Return to Terminal
                  </button>
                </div>
              )}

              {results?.error === "coming_soon" && !loading && (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6 space-y-8 animate-in zoom-in-90">
                  <div className="relative">
                    <AlertTriangle
                      size={100}
                      className="text-yellow-500 animate-pulse"
                    />
                    <div className="absolute inset-0 blur-2xl bg-yellow-500/20" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-yellow-500 font-black text-4xl uppercase italic tracking-tighter">
                      Coming Soon
                    </h3>
                    <p className="text-[10px] text-yellow-700 font-bold uppercase tracking-[0.4em]">
                      THIS FEATURE IS UNDER CONSTRUCTION
                    </p>
                  </div>
                  <button
                    onClick={() => setResults(null)}
                    onMouseEnter={() => sfx("hover")}
                    className="text-[10px] font-bold text-green-800 uppercase underline tracking-widest hover:text-green-400 transition-colors"
                  >
                    Return to Search
                  </button>
                </div>
              )}

              {results?.error === "none" && !loading && (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6 space-y-8 animate-in zoom-in-90">
                  <div className="relative">
                    <ShieldAlert
                      size={100}
                      className="text-red-900 animate-pulse"
                    />
                    <div className="absolute inset-0 blur-2xl bg-red-900/20" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-red-500 font-black text-4xl uppercase italic tracking-tighter">
                      No Active Deal was Found
                    </h3>
                    <p className="text-[10px] text-red-900 font-bold uppercase tracking-[0.4em]">
                      SCAN COMPLETED WITH 0 DEALS
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      sfx("click");
                      setIsAlertModalOpen(true);
                    }}
                    onMouseEnter={() => sfx("hover")}
                    className="w-full max-w-sm border-2 border-green-600 text-green-500 font-black p-5 uppercase hover:bg-green-600 hover:text-black transition-all shadow-[0_0_30px_rgba(34,197,94,0.1)] rounded-lg text-lg tracking-widest relative group overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/10 -translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    <span className="relative z-10">
                      Alert me when discounted
                    </span>
                  </button>
                  <button
                    onClick={() => setResults(null)}
                    onMouseEnter={() => sfx("hover")}
                    className="text-[10px] font-bold text-green-800 uppercase underline tracking-widest hover:text-green-400 transition-colors"
                  >
                    Return to Terminal
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>

        {/* COMPACT CYBER BAR - Email | Privacy | Terms | FAQ */}
        <div className="border-t border-green-500/30 mt-1 pt-3 pb-3 px-4 flex justify-center items-center gap-4 text-[9px] bg-black/40 rounded-b-lg">
          <button
            onClick={() => {
              sfx("click");
              setShowContactModal(true);
            }}
            onMouseEnter={() => sfx("hover")}
            className="flex items-center gap-1.5 text-green-500 hover:text-green-300 transition-all hover:scale-105"
          >
            <Mail size={10} />
            <span className="uppercase font-bold">Email</span>
          </button>

          <span className="text-green-800">|</span>

          <button
            onClick={() => window.open("/privacy.html", "_blank")}
            onMouseEnter={() => sfx("hover")}
            className="flex items-center gap-1.5 text-green-500 hover:text-green-300 transition-all hover:scale-105"
          >
            <ShieldCheck size={10} />
            <span className="uppercase font-bold">Privacy</span>
          </button>

          <span className="text-green-800">|</span>

          <button
            onClick={() => window.open("/terms.html", "_blank")}
            onMouseEnter={() => sfx("hover")}
            className="flex items-center gap-1.5 text-green-500 hover:text-green-300 transition-all hover:scale-105"
          >
            <Scale size={10} />
            <span className="uppercase font-bold">Terms</span>
          </button>

          <span className="text-green-800">|</span>

          <button
            onClick={() => {
              sfx("click");
              setShowFAQModal(true);
            }}
            onMouseEnter={() => sfx("hover")}
            className="flex items-center gap-1.5 text-green-500 hover:text-green-300 transition-all hover:scale-105"
          >
            <Info size={10} />
            <span className="uppercase font-bold">FAQ</span>
          </button>
        </div>

        {/* MOBILE-ONLY FOOTER - Copyright only */}
        <div className="block sm:hidden border-t border-green-500/30 mt-2 pt-3 pb-3 px-4 flex justify-center items-center gap-2 text-[8px] bg-black/40 rounded-b-lg">
          <span className="text-green-600">© 2026 Scan4Keys</span>
          <span className="text-green-800">•</span>
          <span className="text-green-600">All Rights Reserved</span>
        </div>
      </div>

      {/* Left Footer - Powered by Nexarda (Desktop only) */}
      <div className="hidden sm:block sm:fixed sm:bottom-4 sm:left-6 sm:z-[70] sm:pointer-events-none sm:opacity-80 sm:group-hover:sm:opacity-100 sm:transition-opacity mt-2 sm:mt-0">
        <span className="text-[14px] font-black text-green-600 tracking-wide">
          Powered by NEXARDA™
        </span>
      </div>
      {/* Right Footer - Development Build (Desktop only) */}
      <div className="hidden sm:block sm:fixed sm:bottom-4 sm:right-6 sm:z-[70] sm:pointer-events-none sm:opacity-80 sm:group-hover:sm:opacity-100 sm:transition-opacity mt-4 sm:mt-0">
        <div className="flex flex-col items-end gap-1">
          <span className="text-[11px] font-black text-green-600 tracking-[0.5em] uppercase">
            Development_Build
          </span>
          <span className="text-[9px] font-bold text-red-700 uppercase tracking-widest">
            Unstable Environment // Early Access
          </span>
          <span className="text-[9px] text-green-700 font-mono">
            © 2026 Scan4Keys. All rights reserved.
          </span>
        </div>
      </div>

      {/* Legal Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-black border-2 border-green-500 p-6 max-w-md w-full space-y-4 relative rounded-xl shadow-[0_0_100px_rgba(34,197,94,0.2)] animate-in zoom-in-95">
            <div className="absolute top-0 left-0 w-full h-1 bg-green-500/20" />
            <div className="flex items-center justify-between border-b border-green-700 pb-3">
              <h2 className="text-xl font-black uppercase tracking-tight text-green-400">
                MENU
              </h2>
              <button
                onClick={() => setShowLegalModal(false)}
                className="text-green-700 hover:text-green-400"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => window.open("/privacy.html", "_blank")}
                className="w-full text-left p-3 bg-green-950/20 border border-green-700 rounded hover:bg-green-900/40 transition-all flex items-center gap-3"
              >
                <ShieldCheck size={18} className="text-green-500" />
                <span className="font-bold uppercase text-sm">
                  Privacy Policy
                </span>
              </button>
              <button
                onClick={() => window.open("/terms.html", "_blank")}
                className="w-full text-left p-3 bg-green-950/20 border border-green-700 rounded hover:bg-green-900/40 transition-all flex items-center gap-3"
              >
                <Scale size={18} className="text-green-500" />
                <span className="font-bold uppercase text-sm">
                  Terms of Service
                </span>
              </button>
              <button
                onClick={() => {
                  setShowLegalModal(false);
                  setShowContactModal(true);
                }}
                className="w-full text-left p-3 bg-green-950/20 border border-green-700 rounded hover:bg-green-900/40 transition-all flex items-center gap-3"
              >
                <Mail size={18} className="text-green-500" />
                <span className="font-bold uppercase text-sm">Contact</span>
              </button>

              <button
                onClick={() => {
                  setShowLegalModal(false);
                  setShowFAQModal(true);
                }}
                className="w-full text-left p-3 bg-green-950/20 border border-green-700 rounded hover:bg-green-900/40 transition-all flex items-center gap-3"
              >
                <svg
                  className="w-[18px] h-[18px] text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-bold uppercase text-sm">FAQ</span>
              </button>
            </div>
            <div className="pt-3 text-center text-[9px] text-green-800 uppercase tracking-wider">
              Scan4Keys v0.4.2
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-[130] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-black border-2 border-green-500 p-8 max-w-md w-full space-y-6 relative rounded-xl shadow-[0_0_100px_rgba(34,197,94,0.2)] animate-in zoom-in-95">
            <div className="absolute top-0 left-0 w-full h-1 bg-green-500/20" />
            <div className="flex items-center justify-between border-b border-green-700 pb-3">
              <h2 className="text-xl font-black uppercase tracking-tight text-green-400">
                CONTACT
              </h2>
              <button
                onClick={() => setShowContactModal(false)}
                className="text-green-700 hover:text-green-400"
              >
                <X size={20} />
              </button>
            </div>
            <div className="text-center space-y-4">
              <Mail size={48} className="text-green-500 mx-auto" />
              <p className="text-green-400 font-bold text-lg">Email us at:</p>
              <p className="text-green-300 text-xl font-mono bg-black/50 p-3 rounded border border-green-700">
                contact@scan4keys.com
              </p>
              <p className="text-[10px] text-green-700 uppercase tracking-wider">
                We'll get back to you within 48 hours
              </p>
            </div>
            <button
              onClick={() => setShowContactModal(false)}
              className="w-full bg-green-600 text-black font-black py-3 uppercase text-sm hover:bg-green-500 transition-all rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* FAQ Modal */}
      {showFAQModal && (
        <div className="fixed inset-0 z-[130] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-black border-2 border-green-500 p-0 max-w-lg w-full max-h-[80vh] flex flex-col relative rounded-xl shadow-[0_0_100px_rgba(34,197,94,0.2)] animate-in zoom-in-95">
            <div className="absolute top-0 left-0 w-full h-1 bg-green-500/20" />
            {/* Sticky Header */}
            <div className="flex items-center justify-between border-b border-green-700 p-4 sticky top-0 bg-black z-10 rounded-t-xl">
              <h2 className="text-xl font-black uppercase tracking-tight text-green-400">
                ❓ FAQ
              </h2>
              <button
                onClick={() => setShowFAQModal(false)}
                className="text-green-700 hover:text-green-400"
              >
                <X size={20} />
              </button>
            </div>
            {/* Scrollable Content */}
            <div className="overflow-y-auto p-4 space-y-3 custom-scrollbar">
              <div className="bg-green-950/20 border border-green-700 rounded p-3">
                <p className="font-bold text-green-400 text-sm flex items-center gap-2">
                  💰 Why is Scan4Keys free?
                </p>
                <p className="text-green-600 text-xs mt-1 leading-relaxed">
                  We earn small commissions from affiliate links when you buy
                  through our store links. You pay nothing extra.
                </p>
              </div>
              <div className="bg-green-950/20 border border-green-700 rounded p-3">
                <p className="font-bold text-green-400 text-sm flex items-center gap-2">
                  🚫 Why are some prices wrong or outdated?
                </p>
                <p className="text-green-600 text-xs mt-1 leading-relaxed">
                  We use a third-party API (Nexarda). Prices change fast on
                  retailer sites which sometimes causes delay or mistakes.
                  Always verify the final price on the store page before buying.
                </p>
              </div>
              <div className="bg-green-950/20 border border-green-700 rounded p-3">
                <p className="font-bold text-green-400 text-sm flex items-center gap-2">
                  🌍 Why did the link send me to the wrong region?
                </p>
                <p className="text-green-600 text-xs mt-1 leading-relaxed">
                  Region detection isn't perfect. Check the store's
                  region/country selector before purchasing to ensure the price
                  matches.
                </p>
              </div>
              <div className="bg-green-950/20 border border-green-700 rounded p-3">
                <p className="font-bold text-green-400 text-sm flex items-center gap-2">
                  🏪 What stores do you support?
                </p>
                <p className="text-green-600 text-xs mt-1 leading-relaxed">
                  All retailers from the Nexarda API, including Steam, Green Man
                  Gaming, Humble Bundle, Fanatical, and 20+ more.
                </p>
              </div>
              <div className="bg-green-950/20 border border-green-700 rounded p-3">
                <p className="font-bold text-green-400 text-sm flex items-center gap-2">
                  🔒 Is this safe? Do you steal my data?
                </p>
                <p className="text-green-600 text-xs mt-1 leading-relaxed">
                  No accounts. No personal data collected. Ever. Read our
                  Privacy Policy for details.
                </p>
              </div>
              <div className="bg-green-950/20 border border-green-700 rounded p-3">
                <p className="font-bold text-green-400 text-sm flex items-center gap-2">
                  ⚠️ Why do I see errors or glitches?
                </p>
                <p className="text-green-600 text-xs mt-1 leading-relaxed">
                  Scan4Keys is in BETA (v0.4.2). We're actively fixing issues.
                  Report bugs to contact@scan4keys.com
                </p>
              </div>
              <div className="bg-green-950/20 border border-green-700 rounded p-3">
                <p className="font-bold text-green-400 text-sm flex items-center gap-2">
                  📧 How do I contact you?
                </p>
                <p className="text-green-600 text-xs mt-1 leading-relaxed">
                  Email: contact@scan4keys.com
                </p>
              </div>
            </div>
            {/* Sticky Footer Close Button */}
            <div className="p-4 border-t border-green-700 sticky bottom-0 bg-black rounded-b-xl">
              <button
                onClick={() => setShowFAQModal(false)}
                className="w-full bg-green-600 text-black font-black py-3 uppercase text-sm hover:bg-green-500 transition-all rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[140] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-black border-2 border-green-500 p-6 max-w-md w-full space-y-4 relative rounded-xl shadow-[0_0_100px_rgba(34,197,94,0.2)] animate-in zoom-in-95">
            <div className="absolute top-0 left-0 w-full h-1 bg-green-500/20" />
            <div className="flex items-center justify-between border-b border-green-700 pb-3">
              <h2 className="text-xl font-black uppercase tracking-tight text-green-400">
                SHARE GAME
              </h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-green-700 hover:text-green-400"
              >
                <X size={20} />
              </button>
            </div>
            <div className="text-center space-y-4">
              <p className="text-green-400 font-bold text-sm">
                {shareGameTitle}
              </p>
              <div className="bg-black/50 border border-green-700 p-3 rounded flex items-center justify-between gap-2">
                <code className="text-green-500 text-xs break-all font-mono">
                  {`${window.location.origin}/?id=${shareGameId}&title=${encodeURIComponent(shareGameTitle)}`}
                </code>
              </div>
              <button
                onClick={() => {
                  const shareUrl = `${window.location.origin}/?id=${shareGameId}&title=${encodeURIComponent(shareGameTitle)}`;
                  navigator.clipboard.writeText(shareUrl);
                  sfx("click");
                  // Show temporary success message
                  const tempDiv = document.createElement("div");
                  tempDiv.className =
                    "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-black border-2 border-green-500 text-green-400 px-6 py-3 rounded-lg shadow-[0_0_30px_rgba(34,197,94,0.3)] animate-in fade-in slide-in-from-bottom-4 duration-300";
                  tempDiv.innerText = "✓ Link copied to clipboard!";
                  document.body.appendChild(tempDiv);
                  setTimeout(() => tempDiv.remove(), 2000);
                }}
                className="w-full bg-green-600 text-black font-black py-3 uppercase text-sm hover:bg-green-500 transition-all rounded"
              >
                Copy Link
              </button>
            </div>
            <p className="text-[8px] text-green-800 text-center uppercase tracking-wider">
              Share this link with friends to show them the deal
            </p>
          </div>
        </div>
      )}
      {/* Coupon Modal */}
      {showCouponModal && pendingRedirectDeal && (
        <div className="fixed inset-0 z-[150] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
          {/* Animated Background Glow - MEDIUM */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-green-500/10 animate-pulse" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-gradient-radial from-green-500/15 via-transparent to-transparent animate-spin-slow" />
          </div>

          <div className="bg-black border-2 border-green-500 p-0 max-w-md w-full flex flex-col relative rounded-xl shadow-[0_0_60px_rgba(34,197,94,0.25)] animate-in zoom-in-95 duration-300 overflow-hidden">
            {/* SCANLINES - Full Coverage */}
            <div className="absolute inset-0 pointer-events-none z-10 scanlines opacity-15" />

            {/* CRT Flicker Overlay */}
            <div className="absolute inset-0 pointer-events-none z-10 bg-black/5 animate-pulse" />

            {/* Glitch Line (Top) */}
            <div className="absolute top-0 left-0 w-full h-[1px] bg-green-500/40 animate-scanline-fast" />

            {/* Corner Accents */}
            <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-green-500/40 pointer-events-none z-10" />
            <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 border-green-500/40 pointer-events-none z-10" />
            <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 border-green-500/40 pointer-events-none z-10" />
            <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-green-500/40 pointer-events-none z-10" />

            {/* Header */}
            <div className="bg-gradient-to-r from-green-950/80 to-black px-5 py-4 border-b border-green-500/50 rounded-t-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'1\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.05\'/%3E%3C/svg%3E')] opacity-30 pointer-events-none" />
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  {/* Custom SVG Icon - Cyber Tag */}
                  <svg
                    className="w-7 h-7 text-green-500 drop-shadow-[0_0_4px_rgba(34,197,94,0.5)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M6 6h.008v.008H6V6z"
                    />
                  </svg>
                  <h2 className="text-lg md:text-xl font-black uppercase tracking-wider text-green-400">
                    POTENTIAL COUPON FOUND
                  </h2>
                </div>
                {/* Exit button removed */}
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-5 relative">
              {/* Subtle Grid Background */}
              <div className="absolute inset-0 grid-bg opacity-5 pointer-events-none" />

              <p className="text-green-400 text-sm font-mono text-center relative z-10 tracking-wide">
                {pendingRedirectDeal.coupon?.available
                  ? "Use this code for discount:"
                  : pendingRedirectDeal.manualCouponType === 2
                    ? "Use this code - may expire soon:"
                    : "We found a discount code that might be active:"}
              </p>

              {/* Coupon Code Box */}
              <div className="relative group">
                <div className="bg-black/80 border border-green-500/60 rounded-lg p-4 text-center relative z-10 hover:border-green-400/80 transition-all duration-300">
                  <code className="text-green-400 text-2xl md:text-3xl font-mono font-black tracking-wider">
                    {pendingRedirectDeal.coupon?.code || "N/A"}
                  </code>
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-transparent via-green-500/5 to-transparent animate-scanline-fast" />
                </div>
              </div>

              {/* Warning Box */}
              <div className="bg-yellow-950/20 border-l-3 border-yellow-600 p-3">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-500 text-sm"></span>
                  <p className="text-xs text-yellow-500 font-mono leading-relaxed">
                    {pendingRedirectDeal.coupon?.available
                      ? ""
                      : pendingRedirectDeal.manualCouponType === 2
                        ? "Limited time offer. May expire soon."
                        : "NOT GUARANTEED. MAY BE EXPIRED."}
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => {
                  if (pendingRedirectDeal.coupon?.code) {
                    navigator.clipboard.writeText(
                      pendingRedirectDeal.coupon.code,
                    );
                    sfx("click");
                    const tempDiv = document.createElement("div");
                    tempDiv.className =
                      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-black border border-green-500 text-green-400 px-6 py-3 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.25)] animate-in fade-in slide-in-from-bottom-4 duration-300 font-mono text-sm tracking-wide";
                    tempDiv.innerText = "✓ CODE COPIED";
                    document.body.appendChild(tempDiv);
                    setTimeout(() => tempDiv.remove(), 2000);
                  }
                  window.open(pendingRedirectDeal.url, "_blank");
                  setShowCouponModal(false);
                  setPendingRedirectDeal(null);
                  setActiveRedirect(null);
                }}
                className="w-full bg-gradient-to-r from-green-600 to-green-500 text-black font-black py-3.5 uppercase text-sm hover:from-green-500 hover:to-green-400 transition-all duration-300 rounded-md shadow-[0_0_15px_rgba(34,197,94,0.3)] hover:shadow-[0_0_20px_rgba(34,197,94,0.4)] relative group overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative z-10 flex items-center justify-center gap-2">
                  GO TO STORE
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Right-Click Menu - Cyber/Matrix Theme */}
      {contextMenu.visible && (
        <>
          {/* Backdrop glitch effect */}
          <div className="fixed inset-0 z-[299] pointer-events-none">
            <div className="absolute inset-0 bg-green-500/5 animate-pulse" />
          </div>

          <div
            className="fixed z-[300] bg-black border border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.3)] py-1 min-w-[220px]"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onMouseLeave={() =>
              setContextMenu({
                visible: false,
                x: 0,
                y: 0,
                url: "",
                gameTitle: "",
                gameId: "",
                gameImage: "",
              })
            }
          >
            {/* CRT scanline effect on menu */}
            <div className="absolute inset-0 pointer-events-none scanlines opacity-10" />
            <div className="absolute top-0 left-0 w-full h-[1px] bg-green-500/40 animate-scanline-fast" />

            {/* Open in Background Tab (doesn't focus) */}
            <button
              onClick={() => {
                const newTab = window.open(contextMenu.url, "_blank");
                if (newTab) newTab.blur();
                window.focus();
                setContextMenu({
                  visible: false,
                  x: 0,
                  y: 0,
                  url: "",
                  gameTitle: "",
                  gameId: "",
                  gameImage: "",
                });
              }}
              className="w-full text-left px-4 py-2.5 text-green-400 hover:bg-green-950/40 text-[11px] font-mono font-bold uppercase tracking-wider transition-all duration-150 hover:pl-6 hover:text-green-300 group relative overflow-hidden"
            >
              <span className="relative z-10">[ OPEN_IN_BACKGROUND ]</span>
              <div className="absolute inset-0 bg-green-500/10 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
            </button>

            {/* Open in New Tab (focuses) */}
            <button
              onClick={() => {
                window.open(contextMenu.url, "_blank");
                setContextMenu({
                  visible: false,
                  x: 0,
                  y: 0,
                  url: "",
                  gameTitle: "",
                  gameId: "",
                  gameImage: "",
                });
              }}
              className="w-full text-left px-4 py-2.5 text-green-400 hover:bg-green-950/40 text-[11px] font-mono font-bold uppercase tracking-wider transition-all duration-150 hover:pl-6 hover:text-green-300 group relative overflow-hidden"
            >
              <span className="relative z-10">[ OPEN_IN_NEW_TAB ]</span>
              <div className="absolute inset-0 bg-green-500/10 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
            </button>

            {/* Divider */}
            <div className="relative my-1">
              <div className="h-px bg-green-500/30" />
              <div className="absolute left-2 top-1/2 -translate-y-1/2 w-1 h-1 bg-green-500/50 rounded-full" />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 w-1 h-1 bg-green-500/50 rounded-full" />
            </div>

            {/* Copy Link */}
            <button
              onClick={() => {
                const fullUrl = `${window.location.origin}${contextMenu.url}`;
                navigator.clipboard.writeText(fullUrl);
                setContextMenu({
                  visible: false,
                  x: 0,
                  y: 0,
                  url: "",
                  gameTitle: "",
                  gameId: "",
                  gameImage: "",
                });
                const toast = document.createElement("div");
                toast.className =
                  "fixed bottom-6 left-1/2 -translate-x-1/2 bg-black border-2 border-green-500 text-green-400 px-5 py-2 rounded-none text-[10px] font-mono font-bold uppercase tracking-wider z-[301] shadow-[0_0_20px_rgba(34,197,94,0.3)]";
                toast.innerHTML = `> LINK_COPIED: "${contextMenu.gameTitle}"`;
                document.body.appendChild(toast);
                setTimeout(() => toast.remove(), 1800);
              }}
              className="w-full text-left px-4 py-2.5 text-green-400 hover:bg-green-950/40 text-[11px] font-mono font-bold uppercase tracking-wider transition-all duration-150 hover:pl-6 hover:text-green-300 group relative overflow-hidden"
            >
              <span className="relative z-10">[ COPY_LINK ]</span>
              <div className="absolute inset-0 bg-green-500/10 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
            </button>

            {/* Terminal cursor effect at bottom */}
            <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-green-500 to-transparent animate-pulse" />
          </div>
        </>
      )}

      <style>{`
        @keyframes crt-flicker { 0% { opacity: 0.99; } 5% { opacity: 1; } 10% { opacity: 0.992; } 15% { opacity: 0.998; } 100% { opacity: 1; } }
        @keyframes scanline-fast { 0% { transform: translateY(-100%); } 100% { transform: translateY(1000%); } }
        @keyframes title-flicker { 
          0%, 100% { opacity: 1; transform: skew(0deg); filter: brightness(1); }
          92% { opacity: 0.8; transform: skew(0.5deg); filter: brightness(1.2); }
          95% { opacity: 1; transform: skew(-0.5deg); filter: brightness(0.9); }
          98% { opacity: 0.9; transform: skew(1deg); filter: brightness(1.1); }
        }
        @keyframes glitch-slow {
          0%, 100% { transform: translate(0); }
          94% { transform: translate(2px, -1px); }
          95% { transform: translate(-1px, 2px); }
          96% { transform: translate(1px, -1px); }
        }
        @keyframes vhs-tracking {
          0%, 98%, 100% { transform: translateY(0); opacity: 0; }
          99% { transform: translateY(20px); opacity: 0.1; }
        }
        @keyframes swing {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(15deg); }
          40% { transform: rotate(-10deg); }
          60% { transform: rotate(5deg); }
          80% { transform: rotate(-5deg); }
        }
        .animate-scanline-fast { animation: scanline-fast 8s linear infinite; }
        .animate-title-flicker { animation: title-flicker 5s infinite; }
        .animate-glitch-slow { animation: glitch-slow 4s infinite; }
        .animate-pulse-slow { animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        .animate-spin-slow { animation: spin 20s linear infinite; }
        .animate-reverse-spin-slow { animation: spin 15s linear infinite reverse; }
        .animate-swing { animation: swing 2s infinite ease-in-out; }
        .vhs-tracking-jitter { background: linear-gradient(transparent, rgba(255,255,255,0.05), transparent); height: 5px; animation: vhs-tracking 12s infinite linear; }
        .noise-overlay { background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3%3Ffilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"); }
        
        * { scrollbar-width: thin; scrollbar-color: #166534 #000; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: #000; border-left: 1px solid #14532d; }
        ::-webkit-scrollbar-thumb { background: #166534; border: 2px solid #000; border-radius: 0px; }
        ::-webkit-scrollbar-thumb:hover { background: #22c55e; }
        
        .scanlines { background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.2) 50%); background-size: 100% 4px; pointer-events: none; }
        .grid-bg { background-image: linear-gradient(rgba(34, 197, 94, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 197, 94, 0.1) 1px, transparent 1px); background-size: 24px 24px; }
        
        .vignette { background: radial-gradient(circle, rgba(0,0,0,0) 50%, rgba(0,0,0,0.8) 100%); }
        .crt-rolling-wave { background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.15) 50%, rgba(18, 16, 16, 0)); background-size: 100% 30px; height: 100%; width: 100%; z-index: 62; opacity: 0.1; animation: crt-wave-move 15s linear infinite; }
                #beta-carousel {
          scroll-behavior: smooth;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scroll-duration: 0.8s;
        }
        @keyframes crt-wave-move { 0% { transform: translateY(-100%); } 100% { transform: translateY(100%); } }
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee {
          animation: marquee 15s linear infinite;
          display: inline-block;
        }
        @keyframes marquee-infinite {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .animate-marquee-infinite {
          animation: marquee-infinite 10s linear infinite;
          display: inline-block;
        }
        
        /* HEAVY GLITCH ANIMATION */
        @keyframes heavyGlitch {
          0% { transform: translate(0, 0); filter: none; opacity: 1; }
          5% { transform: translate(-8px, 2px); filter: hue-rotate(90deg); opacity: 0.9; }
          10% { transform: translate(8px, -2px); filter: hue-rotate(180deg); opacity: 0.8; }
          15% { transform: translate(-4px, 4px); filter: hue-rotate(270deg); opacity: 0.7; }
          20% { transform: translate(4px, -4px); filter: hue-rotate(360deg); opacity: 0.6; }
          25% { transform: translate(-2px, 0); filter: brightness(2) contrast(200%); opacity: 0.5; }
          30% { transform: translate(2px, 0); filter: brightness(0.5) contrast(50%); opacity: 0.6; }
          35% { transform: translate(-10px, 3px); filter: blur(2px); opacity: 0.4; }
          40% { transform: translate(10px, -3px); filter: blur(4px); opacity: 0.3; }
          45% { transform: translate(0, 0); filter: blur(0px); opacity: 0.5; }
          50% { transform: translate(0, 0); filter: none; opacity: 0.7; }
          55% { transform: translate(-5px, 0); filter: hue-rotate(180deg) brightness(1.5); opacity: 0.8; }
          60% { transform: translate(5px, 0); filter: hue-rotate(0deg) brightness(0.8); opacity: 0.9; }
          65% { transform: translate(0, 0); filter: none; opacity: 1; }
          70% { transform: translate(0, 0); filter: none; opacity: 1; }
          100% { transform: translate(0, 0); filter: none; opacity: 1; }
        }
        .animate-heavy-glitch {
          animation: heavyGlitch 1.2s ease-in-out;
        }
        
        /* TV Shutdown Animation */
        @keyframes tvShutdown {
          0% { transform: scale(1); opacity: 1; filter: none; }
          10% { transform: scale(0.98); opacity: 0.9; filter: brightness(1.1) contrast(1.2); }
          30% { transform: scale(0.5) translateY(0); opacity: 0.5; filter: brightness(0.3) contrast(2); }
          50% { transform: scale(0.05) translateY(0); opacity: 0.2; filter: brightness(0) contrast(5); }
          55% { transform: scale(0.02) translateY(0); opacity: 0; filter: brightness(0); }
          65% { transform: scale(0.02); opacity: 0; }
          70% { transform: scale(0.05); opacity: 0.1; filter: blur(3px) brightness(0.5); }
          75% { transform: scale(0.5); opacity: 0.5; filter: blur(2px) brightness(0.8); }
          80% { transform: scale(0.95); opacity: 0.8; filter: blur(1px); }
          85% { transform: scale(1); opacity: 1; filter: blur(0px); }
          90% { text-shadow: -2px 0 red, 2px 0 blue; }
          95% { text-shadow: 2px 0 red, -2px 0 blue; }
          100% { text-shadow: none; }
        }
        .animate-tv-shutdown {
          animation: tvShutdown 0.8s ease-in-out;
        }
        
        /* RGB Split Glitch Animation */
        @keyframes rgbSplit {
          0%, 85%, 100% { text-shadow: none; filter: none; }
          88% { text-shadow: -2px 0 red, 2px 0 blue; filter: blur(0px); }
          91% { text-shadow: 2px 0 red, -2px 0 blue; filter: blur(0.5px); }
          94% { text-shadow: -1px 0 cyan, 1px 0 magenta; filter: blur(0px); }
          97% { text-shadow: none; filter: blur(0.3px); }
        }
        .animate-rgb-split {
          animation: rgbSplit 0.3s ease-in-out infinite;
        }
                @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
                        .exposure-layer {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 999;
          backdrop-filter: brightness(1.1) contrast(1);
        }
        
        /* Subtle Green Tint Layer - Separate from exposure */
        .green-tint {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 998;
          background: rgba(34, 197, 94, 0.004);
          mix-blend-mode: screen;
        }
          <style>
        @keyframes hologlobe-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes radar-sweep {
          0% { transform: rotate(0deg); opacity: 1; }
          100% { transform: rotate(360deg); opacity: 0.3; }
        }
        @keyframes particle-float {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-150px) scale(0); opacity: 0; }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.5); opacity: 0.8; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes scan-flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
          25%, 75% { opacity: 0.85; }
        }
        .hologlobe-container {
          position: relative;
          width: 180px;
          height: 180px;
          margin: 0 auto;
          animation: scan-flicker 0.15s infinite;
        }
        .globe-wireframe {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 2px solid #22c55e;
          box-shadow: 0 0 20px rgba(34,197,94,0.5), inset 0 0 20px rgba(34,197,94,0.2);
          animation: hologlobe-spin 4s linear infinite;
        }
        .globe-lat {
          position: absolute;
          inset: 20%;
          border-radius: 50%;
          border: 1px solid #22c55e80;
          animation: hologlobe-spin 6s linear infinite reverse;
        }
        .globe-lon {
          position: absolute;
          inset: 10%;
          border-radius: 50%;
          border: 1px dashed #22c55e60;
          animation: hologlobe-spin 3s linear infinite;
        }
        .radar-line {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 50%;
          height: 2px;
          background: linear-gradient(90deg, #22c55e, transparent);
          transform-origin: 0% 50%;
          animation: radar-sweep 2s linear infinite;
          box-shadow: 0 0 10px #22c55e;
        }
        .pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100px;
          height: 100px;
          margin-left: -50px;
          margin-top: -50px;
          border-radius: 50%;
          border: 2px solid #22c55e;
          animation: pulse-ring 1.5s ease-out infinite;
          pointer-events: none;
        }
        .pulse-ring:nth-child(2) { animation-delay: 0.5s; }
        .pulse-ring:nth-child(3) { animation-delay: 1s; }
        .particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #22c55e;
          border-radius: 50%;
          box-shadow: 0 0 5px #22c55e;
          animation: particle-float 1.5s linear infinite;
        }
                  /* Glitch text animation */
        @keyframes glitch-text {
          0%, 100% { text-shadow: none; transform: skew(0deg); }
          5% { text-shadow: -2px 0 red, 2px 0 blue; transform: skew(2deg); }
          10% { text-shadow: 2px 0 red, -2px 0 blue; transform: skew(-2deg); }
          15% { text-shadow: none; transform: skew(0deg); }
        }
        .glitch-text {
          animation: glitch-text 0.5s infinite;
          text-shadow: 0 0 10px #22c55e, 0 0 20px #22c55e, 0 0 30px #22c55e;
        }
                  /* Static noise flash */
        @keyframes static-flash {
          0%, 100% { opacity: 0; background: none; }
          2% { opacity: 0.4; background: repeating-linear-gradient(45deg, #22c55e20 0px, #22c55e20 2px, transparent 2px, transparent 8px); }
          3% { opacity: 0; background: none; }
          14% { opacity: 0.3; background: repeating-linear-gradient(-45deg, #22c55e20 0px, #22c55e20 3px, transparent 3px, transparent 10px); }
          15% { opacity: 0; background: none; }
          27% { opacity: 0.5; background: repeating-linear-gradient(90deg, #22c55e30 0px, #22c55e30 4px, transparent 4px, transparent 12px); }
          28% { opacity: 0; background: none; }
          47% { opacity: 0.35; background: repeating-linear-gradient(135deg, #22c55e20 0px, #22c55e20 2px, transparent 2px, transparent 6px); }
          48% { opacity: 0; background: none; }
          67% { opacity: 0.45; background: repeating-linear-gradient(0deg, #22c55e30 0px, #22c55e30 3px, transparent 3px, transparent 9px); }
          68% { opacity: 0; background: none; }
          87% { opacity: 0.3; background: repeating-linear-gradient(60deg, #22c55e20 0px, #22c55e20 2px, transparent 2px, transparent 7px); }
          88% { opacity: 0; background: none; }
        }
        /* Dots appear together, vanish together */
        .dots-animated {
          animation: dots-together 2s infinite;
          display: inline-block;
          min-width: 45px;
          text-align: left;
        }
        @keyframes dots-together {
          0%, 100% { opacity: 1; letter-spacing: 0px; }
          50% { opacity: 0; letter-spacing: 0px; }
        }
                  /* RGB shift animation for extra glitch */
        @keyframes rgb-shift {
          0%, 100% { filter: drop-shadow(0px 0px 0px red) drop-shadow(0px 0px 0px blue); }
          10% { filter: drop-shadow(-3px 0px 0px red) drop-shadow(3px 0px 0px blue); }
          20% { filter: drop-shadow(2px 0px 0px red) drop-shadow(-2px 0px 0px blue); }
          30% { filter: drop-shadow(-1px 0px 0px red) drop-shadow(1px 0px 0px blue); }
          40% { filter: drop-shadow(0px 0px 0px red) drop-shadow(0px 0px 0px blue); }
          50% { filter: drop-shadow(3px 0px 0px red) drop-shadow(-3px 0px 0px blue); }
          60% { filter: drop-shadow(-2px 0px 0px red) drop-shadow(2px 0px 0px blue); }
          70% { filter: drop-shadow(1px 0px 0px red) drop-shadow(-1px 0px 0px blue); }
          80% { filter: drop-shadow(0px 0px 0px red) drop-shadow(0px 0px 0px blue); }
          90% { filter: drop-shadow(-3px 0px 0px red) drop-shadow(3px 0px 0px blue); }
        }
                  /* REAL VISUAL GLITCH - sudden jumps, no smooth transitions */
                /* MILD GLITCH - barely noticeable */
        @keyframes mild-glitch {
          0%, 100% { transform: translate(0, 0); filter: none; }
          3% { transform: translate(-2px, 1px); filter: hue-rotate(5deg); }
          4% { transform: translate(2px, -1px); filter: hue-rotate(-5deg); }
          5% { transform: translate(0, 0); filter: none; }
          15% { transform: translate(0, 0); filter: none; }
          18% { transform: translate(1px, -1px); filter: brightness(1.05); }
          19% { transform: translate(-1px, 1px); filter: brightness(0.95); }
          20% { transform: translate(0, 0); filter: none; }
          35% { transform: translate(0, 0); filter: none; }
          38% { transform: translate(-1px, 2px); filter: hue-rotate(3deg); }
          39% { transform: translate(1px, -1px); filter: hue-rotate(-3deg); }
          40% { transform: translate(0, 0); filter: none; }
          60% { transform: translate(0, 0); filter: none; }
          63% { transform: translate(2px, -1px); filter: brightness(1.03); }
          64% { transform: translate(-1px, 1px); filter: contrast(1.05); }
          65% { transform: translate(0, 0); filter: none; }
          85% { transform: translate(0, 0); filter: none; }
          88% { transform: translate(-1px, 1px); filter: hue-rotate(4deg); }
          89% { transform: translate(1px, -1px); filter: brightness(0.97); }
          90% { transform: translate(0, 0); filter: none; }
        }
        .animate-slow-frame-glitch {
          animation: mild-glitch 4s ease-in-out infinite;
        }
                  @keyframes fall {
          0% { transform: translateY(-100%); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(1000%); opacity: 0; }
        }
        
        @keyframes loading-bar {
          0% { width: 0%; margin-left: 0%; }
          50% { width: 100%; margin-left: 0%; }
          100% { width: 0%; margin-left: 100%; }
        }
        
        @keyframes glitch-1 {
          0%, 100% { transform: translate(0); opacity: 0; }
          10% { transform: translate(-2px, 1px); opacity: 0.6; }
          20% { transform: translate(2px, -1px); opacity: 0.4; }
          30% { transform: translate(-1px, 0); opacity: 0.5; }
          40% { transform: translate(1px, 1px); opacity: 0.3; }
          50% { transform: translate(0, -1px); opacity: 0; }
        }
        
        @keyframes glitch-2 {
          0%, 100% { transform: translate(0); opacity: 0; }
          15% { transform: translate(2px, -1px); opacity: 0.5; }
          25% { transform: translate(-2px, 1px); opacity: 0.4; }
          35% { transform: translate(1px, 0); opacity: 0.6; }
          45% { transform: translate(-1px, -1px); opacity: 0.3; }
          55% { transform: translate(0, 1px); opacity: 0; }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 40px rgba(34,197,94,0.3), 0 0 60px rgba(34,197,94,0.1); }
          50% { box-shadow: 0 0 60px rgba(34,197,94,0.6), 0 0 90px rgba(34,197,94,0.2); }
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .animate-glitch-1 {
          animation: glitch-1 1.5s infinite;
        }
        
        .animate-glitch-2 {
          animation: glitch-2 1.5s infinite;
        }
        
        @keyframes rgb-shift {
          0%, 100% { background: linear-gradient(90deg, rgba(255,0,0,0) 0%, rgba(0,255,0,0) 50%, rgba(0,0,255,0) 100%); }
          25% { background: linear-gradient(90deg, rgba(255,0,0,0.1) 0%, rgba(0,255,0,0) 50%, rgba(0,0,255,0) 100%); }
          50% { background: linear-gradient(90deg, rgba(255,0,0,0) 0%, rgba(0,255,0,0.1) 50%, rgba(0,0,255,0) 100%); }
          75% { background: linear-gradient(90deg, rgba(255,0,0,0) 0%, rgba(0,255,0,0) 50%, rgba(0,0,255,0.1) 100%); }
        }
        
        @keyframes static-flash {
          0%, 90%, 100% { opacity: 0; }
          95% { opacity: 0.15; background: white; }
          97% { opacity: 0.05; background: white; }
        }
        
        .animate-static-flash {
          animation: static-flash 3s infinite;
        }
        
        @keyframes scanline-fast {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-spin-slow {
          animation: spin-slow 4s linear infinite;
        }
      `}</style>
    </div>
  );
}
